import React, { useEffect, useState } from "react";
import { Shell } from "@/components/Shell";
import { api, fmtDate, dollars } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { refreshSiteAssets } from "@/lib/siteAssets";
import { refreshSiteContent } from "@/lib/siteContent";
import { imageUrl, ImageUpload, SingleImageUpload } from "@/components/ImageUpload";
import { EventInfoFieldset } from "@/components/EventInfoFieldset";
import { LayoutDashboard, CalendarDays, Tag, Layers, Users, Trash2, Mail, Image as ImageIcon, Type, Grid as GridIcon, Pencil, X, BarChart3, Send, Archive, ArchiveRestore, UserPlus, Link as LinkIcon, Eye, RefreshCw, Gift } from "lucide-react";

const TABS = [
    { id: "metrics", label: "Overview", icon: LayoutDashboard },
    { id: "events", label: "Events", icon: CalendarDays },
    { id: "categories", label: "Categories", icon: Tag },
    { id: "boothelements", label: "Booth elements", icon: GridIcon },
    { id: "tiers", label: "Pricing tiers", icon: Layers },
    { id: "users", label: "Users", icon: Users },
    { id: "refunds", label: "Refunds", icon: Layers },
    { id: "reviews", label: "Reviews", icon: Type },
    { id: "seo", label: "SEO", icon: BarChart3 },
    { id: "suppression", label: "Email safety", icon: Mail },
    { id: "emails", label: "Email templates", icon: Mail },
    { id: "assets", label: "Site assets", icon: ImageIcon },
    { id: "content", label: "Homepage text", icon: Type },
];

const Metric = ({ label, value, hint, accent = "forest" }) => (
    <div className={`rounded-xl border border-brand-line bg-white p-5`}>
        <div className="label-base">{label}</div>
        <div className={`font-heading text-3xl font-extrabold ${accent === "coral" ? "text-brand-coral" : "text-brand-forest"} mt-1`}>{value}</div>
        {hint && <div className="text-xs text-brand-mute mt-1">{hint}</div>}
    </div>
);

export default function AdminDashboard() {
    const { user: currentUser } = useAuth();
    const [tab, setTab] = useState("metrics");
    const [metrics, setMetrics] = useState(null);
    const [events, setEvents] = useState([]);
    const [cats, setCats] = useState([]);
    const [tiers, setTiers] = useState([]);
    const [users, setUsers] = useState([]);
    const [stripeStatus, setStripeStatus] = useState(null);
    const [reviewing, setReviewing] = useState(null); // event being reviewed
    const [tierId, setTierId] = useState("");
    const [overrides, setOverrides] = useState({});
    // Support edit modals
    const [editingUser, setEditingUser] = useState(null);
    const [editingEvent, setEditingEvent] = useState(null);
    const [editingVendor, setEditingVendor] = useState(null);
    // Admin-created draft vendors: full list (claimed + unclaimed + archived)
    // loaded on demand so the "Unclaimed vendor profiles" panel at the top
    // of the Users tab can render status badges + claim/archive actions.
    const [vendorProfiles, setVendorProfiles] = useState([]);
    const [creatingVendor, setCreatingVendor] = useState(false);
    const [sendClaimFor, setSendClaimFor] = useState(null);
    // Admin event detail modal (parity with host view: applications, pitch
    // messages, payments, pricing, conversations). Keeps the events table
    // uncluttered — one "View" button opens the deep-dive.
    const [viewingEventId, setViewingEventId] = useState(null);

    const reloadAfterEdit = async () => { await loadAll(); };

    const openVendorEdit = async (u) => {
        try {
            const res = await api.get("/admin/vendor-profiles");
            const vp = res.data.find((v) => v.user_id === u.id || v.user?.id === u.id);
            if (!vp) return toast.error("This vendor has not completed their profile yet.");
            setEditingVendor({ ...vp, _user_email: u.email });
        } catch (err) { toast.error(err?.response?.data?.detail || "Could not load vendor profile"); }
    };

    // Open the EditVendorModal directly from a vendor-profile row (used for
    // admin-created drafts that have no `user` record yet).
    const openVendorEditDirect = (vp) => {
        setEditingVendor({ ...vp, _user_email: vp.user?.email || vp.pending_claim_email || "" });
    };

    const archiveVendor = async (vp) => {
        if (!window.confirm(`Archive "${vp.business_name}"? It will be hidden from the public directory but remain recoverable here.`)) return;
        try {
            await api.post(`/admin/vendor-profiles/${vp.id}/archive`);
            toast.success("Profile archived");
            loadAll();
        } catch (err) { toast.error(err?.response?.data?.detail || "Archive failed"); }
    };

    const restoreVendor = async (vp) => {
        try {
            await api.post(`/admin/vendor-profiles/${vp.id}/restore`);
            toast.success("Profile restored");
            loadAll();
        } catch (err) { toast.error(err?.response?.data?.detail || "Restore failed"); }
    };

    const resendClaim = async (vp) => {
        try {
            await api.post(`/admin/vendor-profiles/${vp.id}/resend-claim`);
            toast.success(`Resent invitation to ${vp.pending_claim_email}`);
            loadAll();
        } catch (err) { toast.error(err?.response?.data?.detail || "Resend failed"); }
    };

    // Copy the active claim URL to the clipboard. Useful when the vendor's
    // email is bouncing, in spam, or (on preview) blocked by the allowlist —
    // admin can share the URL directly via Slack/SMS/etc.
    const copyClaimLink = async (vp) => {
        try {
            const res = await api.get(`/admin/vendor-profiles/${vp.id}/active-claim-link`);
            const url = res.data?.url;
            if (!url) throw new Error("No URL");
            try {
                await navigator.clipboard.writeText(url);
                toast.success("Claim link copied to clipboard");
            } catch {
                // Some browsers (or non-secure contexts) block clipboard writes.
                // Fall back to surfacing the URL so the admin can copy manually.
                window.prompt("Copy this claim link:", url);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "No active claim link — send an invitation first.");
        }
    };

    const loadAll = async () => {
        const [m, e, c, t, u, s, vp] = await Promise.all([
            api.get("/admin/metrics"),
            api.get("/admin/events"),
            api.get("/categories"),
            api.get("/tiers"),
            api.get("/admin/users"),
            api.get("/admin/stripe/status").catch(() => ({ data: null })),
            api.get("/admin/vendor-profiles"),
        ]);
        setMetrics(m.data); setEvents(e.data); setCats(c.data); setTiers(t.data); setUsers(u.data);
        setStripeStatus(s.data);
        setVendorProfiles(vp.data || []);
    };
    useEffect(() => { loadAll(); }, []);

    const openReview = (ev) => {
        setReviewing(ev);
        setTierId(tiers[0]?.id || "");
        setOverrides({});
    };

    const publish = async () => {
        if (!tierId) return toast.error("Pick a tier");
        try {
            await api.post(`/admin/events/${reviewing.id}/publish`, {
                tier_id: tierId,
                category_overrides: Object.fromEntries(Object.entries(overrides).filter(([, v]) => v).map(([k, v]) => [k, parseInt(v, 10) * 100])),
            });
            toast.success("Event published & vendors notified");
            setReviewing(null);
            loadAll();
        } catch (err) { toast.error(err?.response?.data?.detail || "Publish failed"); }
    };

    const archive = async (id) => {
        await api.post(`/admin/events/${id}/archive`);
        toast.success("Archived"); loadAll();
    };

    const deleteEvent = async (id, name) => {
        if (!window.confirm(`Delete event "${name}"?\n\nThis permanently removes the event, all applications, payment records, and related notifications. This cannot be undone.`)) return;
        try {
            const res = await api.delete(`/admin/events/${id}`);
            toast.success(`Deleted event + ${res.data.applications_deleted} applications, ${res.data.notifications_deleted} notifications`);
            loadAll();
        } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
    };

    const deleteUser = async (u) => {
        const label = u.role === "host_company" ? "host (and ALL their events)" : u.role === "vendor" ? "vendor (and their applications)" : "user";
        if (!window.confirm(`Delete ${label} "${u.email}"?\n\nThis is permanent and cascades to profiles, events, applications, notifications and uploads. Cannot be undone.`)) return;
        try {
            const res = await api.delete(`/admin/users/${u.id}`);
            const bits = [];
            if (res.data.events_deleted) bits.push(`${res.data.events_deleted} events`);
            if (res.data.applications_deleted) bits.push(`${res.data.applications_deleted} applications`);
            if (res.data.notifications_deleted) bits.push(`${res.data.notifications_deleted} notifications`);
            toast.success(`Deleted ${u.email}` + (bits.length ? ` (also: ${bits.join(", ")})` : ""));
            loadAll();
        } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
    };

    const saveCat = async (cat) => {
        if (cat.id) await api.patch(`/admin/categories/${cat.id}`, cat);
        else await api.post(`/admin/categories`, cat);
        toast.success("Saved"); loadAll();
    };
    const deleteCat = async (id) => {
        if (!window.confirm("Delete this category?")) return;
        await api.delete(`/admin/categories/${id}`); loadAll();
    };

    const saveTier = async (t) => {
        if (t.id) await api.patch(`/admin/tiers/${t.id}`, t);
        else await api.post(`/admin/tiers`, t);
        toast.success("Saved"); loadAll();
    };

    return (
        <Shell>
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
                <div className="label-base">Admin</div>
                <h1 className="font-heading text-3xl md:text-4xl font-extrabold">Control room</h1>

                <div className="mt-6 flex flex-wrap gap-2 border-b border-brand-line">
                    {TABS.map((t) => (
                        <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
                            className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t.id ? "border-brand-forest text-brand-forest" : "border-transparent text-brand-mute hover:text-brand-ink"}`}>
                            <t.icon size={16} /> {t.label}
                        </button>
                    ))}
                </div>

                <div className="mt-8">
                    {tab === "metrics" && metrics && (
                        <div className="space-y-6">
                            <StripeStatusBanner status={stripeStatus} />
                            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                <Metric label="Total events" value={metrics.total_events} />
                                <Metric label="Published" value={metrics.published_events} />
                                <Metric label="Vendors" value={metrics.total_vendors} accent="coral" />
                                <Metric label="Hosts" value={metrics.total_hosts} accent="coral" />
                                <Metric label="Applications" value={metrics.total_applications} />
                                <Metric label="Paid" value={metrics.paid_applications} />
                                <Metric label="Revenue" value={dollars(metrics.revenue_cents)} accent="coral" hint="Sum of paid booth fees" />
                            </div>
                        </div>
                    )}

                    {tab === "events" && (
                        <div className="rounded-xl border border-brand-line bg-white overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-brand-bone text-brand-mute">
                                    <tr><th className="text-left px-4 py-3">Event</th><th>Date</th><th>Host</th><th>Status</th><th>Applications</th><th></th></tr>
                                </thead>
                                <tbody>
                                    {events.map((ev) => (
                                        <tr key={ev.id} className="border-t border-brand-line" data-testid={`admin-event-${ev.id}`}>
                                            <td className="px-4 py-3 font-semibold">{ev.name}</td>
                                            <td className="text-center">{fmtDate(ev.event_date)}</td>
                                            <td className="text-center">{ev.host?.company_name || "—"}</td>
                                            <td className="text-center"><span className="chip">{ev.status.replace("_", " ")}</span></td>
                                            <td className="text-center">{ev.application_count}</td>
                                            <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                                                <button data-testid={`view-event-${ev.id}`} onClick={() => setViewingEventId(ev.id)} className="pill px-3 py-1.5 text-xs font-semibold border border-brand-forest/40 text-brand-forest bg-white hover:bg-brand-forest/5 inline-flex items-center gap-1">
                                                    <Eye size={12} /> View
                                                </button>
                                                {ev.status === "pending_review" && <button data-testid={`review-${ev.id}`} onClick={() => openReview(ev)} className="pill btn-coral px-3 py-1.5 text-xs font-semibold">Review & price</button>}
                                                {ev.status === "published" && <button onClick={() => archive(ev.id)} className="pill px-3 py-1.5 text-xs font-semibold border border-brand-line bg-white hover:bg-brand-bone">Archive</button>}
                                                <button data-testid={`edit-event-${ev.id}`} onClick={() => setEditingEvent(ev)} className="pill px-3 py-1.5 text-xs font-semibold border border-brand-forest/40 text-brand-forest bg-white hover:bg-brand-forest/5 inline-flex items-center gap-1">
                                                    <Pencil size={12} /> Edit
                                                </button>
                                                <button data-testid={`delete-event-${ev.id}`} onClick={() => deleteEvent(ev.id, ev.name)} className="pill px-3 py-1.5 text-xs font-semibold text-brand-coral border border-brand-coral/40 bg-white hover:bg-brand-coral/5 inline-flex items-center gap-1">
                                                    <Trash2 size={12} /> Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {events.length === 0 && <div className="p-8 text-center text-brand-mute">No events yet.</div>}
                        </div>
                    )}

                    {tab === "categories" && (
                        <CategoriesTab cats={cats} onSave={saveCat} onDelete={deleteCat} />
                    )}

                    {tab === "tiers" && (
                        <TiersTab tiers={tiers} onSave={saveTier} />
                    )}

                    {tab === "users" && (
                        <div className="space-y-6">
                            <VendorProfilesPanel
                                profiles={vendorProfiles}
                                onCreate={() => setCreatingVendor(true)}
                                onEdit={openVendorEditDirect}
                                onSendClaim={(vp) => setSendClaimFor(vp)}
                                onResendClaim={resendClaim}
                                onCopyClaimLink={copyClaimLink}
                                onArchive={archiveVendor}
                                onRestore={restoreVendor}
                            />

                            <div className="rounded-xl border border-brand-line bg-white overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-brand-bone text-brand-mute"><tr><th className="text-left px-4 py-3">Name</th><th className="text-left">Email</th><th>Role</th><th>Active</th><th></th></tr></thead>
                                <tbody>
                                    {users.map((u) => (
                                        <tr key={u.id} className="border-t border-brand-line">
                                            <td className="px-4 py-3 font-semibold">{u.full_name}</td>
                                            <td>{u.email}</td>
                                            <td className="text-center"><span className="chip">{u.role}</span></td>
                                            <td className="text-center">{u.is_active ? "✓" : "—"}</td>
                                            <td className="text-right pr-4 whitespace-nowrap space-x-2">
                                                <button data-testid={`edit-user-${u.id}`} onClick={() => setEditingUser(u)} className="pill px-3 py-1.5 text-xs font-semibold border border-brand-forest/40 text-brand-forest bg-white hover:bg-brand-forest/5 inline-flex items-center gap-1">
                                                    <Pencil size={12} /> Edit
                                                </button>
                                                {u.role === "vendor" && (
                                                    <button data-testid={`edit-vendor-profile-${u.id}`} onClick={() => openVendorEdit(u)} className="pill px-3 py-1.5 text-xs font-semibold border border-brand-line bg-white hover:bg-brand-bone">
                                                        Edit profile
                                                    </button>
                                                )}
                                                {u.id === currentUser?.id ? (
                                                    <span className="text-xs text-brand-mute italic">you</span>
                                                ) : (
                                                    <button data-testid={`delete-user-${u.id}`} onClick={() => deleteUser(u)} className="pill px-3 py-1.5 text-xs font-semibold text-brand-coral border border-brand-coral/40 bg-white hover:bg-brand-coral/5 inline-flex items-center gap-1">
                                                        <Trash2 size={12} /> Delete
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            </div>
                        </div>
                    )}

                    {tab === "emails" && (
                        <EmailTemplatesTab adminEmail={currentUser?.email} />
                    )}

                    {tab === "assets" && (
                        <SiteAssetsTab />
                    )}

                    {tab === "content" && (
                        <SiteContentTab />
                    )}

                    {tab === "boothelements" && (
                        <BoothElementsTab />
                    )}

                    {tab === "refunds" && (
                        <RefundsTab />
                    )}

                    {tab === "reviews" && (
                        <ReviewsTab />
                    )}

                    {tab === "seo" && (
                        <SeoTab />
                    )}

                    {tab === "suppression" && (
                        <SuppressionTab />
                    )}
                </div>
            </div>

            {reviewing && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="review-modal">
                    <div className="bg-white rounded-2xl max-w-2xl w-full p-8 max-h-[90vh] overflow-auto">
                        <h2 className="font-heading text-2xl font-extrabold">Review: {reviewing.name}</h2>
                        <p className="text-sm text-brand-mute mt-1">{fmtDate(reviewing.event_date)} · {reviewing.host?.company_name}</p>

                        <div className="mt-6">
                            <label className="label-base">Pricing tier</label>
                            <select data-testid="review-tier" className="input-base" value={tierId} onChange={(e) => setTierId(e.target.value)}>
                                {tiers.map((t) => <option key={t.id} value={t.id}>{t.name} (+{t.markup_pct}%)</option>)}
                            </select>
                        </div>

                        <div className="mt-6">
                            <div className="label-base">Per-category override (USD, optional)</div>
                            <div className="space-y-2">
                                {(reviewing.desired_categories || []).map((cid) => {
                                    const c = cats.find((x) => x.id === cid);
                                    if (!c) return null;
                                    const tier = tiers.find((t) => t.id === tierId);
                                    const calc = tier ? Math.round(c.baseline_price_cents * (1 + tier.markup_pct / 100)) : c.baseline_price_cents;
                                    return (
                                        <div key={cid} className="flex items-center gap-3">
                                            <div className="flex-1">
                                                <div className="font-bold">{c.name}</div>
                                                <div className="text-xs text-brand-mute">Calculated: {dollars(calc)}</div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-brand-mute">$</span>
                                                <input data-testid={`override-${cid}`} type="number" min={0} placeholder="override" className="input-base w-32"
                                                    value={overrides[cid] || ""} onChange={(e) => setOverrides({ ...overrides, [cid]: e.target.value })} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="mt-8 flex justify-end gap-3">
                            <button onClick={() => setReviewing(null)} className="pill px-5 py-3 font-semibold border border-brand-line bg-white">Cancel</button>
                            <button data-testid="publish-event" onClick={publish} className="pill btn-coral px-5 py-3 font-semibold">Publish & notify vendors</button>
                        </div>
                    </div>
                </div>
            )}

            {editingUser && (
                <EditUserModal
                    user={editingUser}
                    onClose={() => setEditingUser(null)}
                    onSaved={async () => { setEditingUser(null); await reloadAfterEdit(); }}
                />
            )}
            {editingEvent && (
                <EditEventModal
                    event={editingEvent}
                    cats={cats}
                    hosts={users.filter((u) => u.role === "host_company")}
                    onClose={() => setEditingEvent(null)}
                    onSaved={async () => { setEditingEvent(null); await reloadAfterEdit(); }}
                />
            )}
            {editingVendor && (
                <EditVendorModal
                    vendor={editingVendor}
                    cats={cats}
                    onClose={() => setEditingVendor(null)}
                    onSaved={async () => { setEditingVendor(null); await reloadAfterEdit(); }}
                />
            )}
            {creatingVendor && (
                <CreateVendorModal
                    cats={cats}
                    onClose={() => setCreatingVendor(false)}
                    onSaved={async () => { setCreatingVendor(false); await reloadAfterEdit(); }}
                />
            )}
            {sendClaimFor && (
                <SendClaimModal
                    vendor={sendClaimFor}
                    events={events}
                    onClose={() => setSendClaimFor(null)}
                    onSaved={async () => { setSendClaimFor(null); await reloadAfterEdit(); }}
                />
            )}
            {viewingEventId && (
                <AdminEventDetailModal
                    eventId={viewingEventId}
                    onClose={() => setViewingEventId(null)}
                />
            )}
        </Shell>
    );
}

const CategoriesTab = ({ cats, onSave, onDelete }) => {
    const [draft, setDraft] = useState({ name: "", baseline_price_cents: 25000, is_active: true });
    return (
        <div className="space-y-6">
            <div className="rounded-xl border border-brand-line bg-white p-6">
                <h3 className="font-heading font-bold">Add category</h3>
                <div className="flex flex-wrap gap-3 mt-3">
                    <input data-testid="cat-name" className="input-base max-w-xs" placeholder="Name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                    <input data-testid="cat-price" type="number" min={0} className="input-base max-w-xs" placeholder="Baseline price (cents)" value={draft.baseline_price_cents} onChange={(e) => setDraft({ ...draft, baseline_price_cents: parseInt(e.target.value || "0", 10) })} />
                    <button data-testid="cat-add" onClick={() => { onSave(draft); setDraft({ name: "", baseline_price_cents: 25000, is_active: true }); }} className="pill btn-coral px-5 h-11 font-semibold">Add</button>
                </div>
            </div>
            <div className="rounded-xl border border-brand-line bg-white overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-brand-bone text-brand-mute"><tr><th className="text-left px-4 py-3">Name</th><th>Baseline</th><th>Active</th><th></th></tr></thead>
                    <tbody>
                        {cats.map((c) => (
                            <tr key={c.id} className="border-t border-brand-line">
                                <td className="px-4 py-3 font-semibold">{c.name}</td>
                                <td className="text-center">{dollars(c.baseline_price_cents)}</td>
                                <td className="text-center">{c.is_active ? "✓" : "—"}</td>
                                <td className="text-right px-4">
                                    <button onClick={() => onDelete(c.id)} className="text-xs text-brand-coral hover:underline">Delete</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const TiersTab = ({ tiers, onSave }) => {
    const [drafts, setDrafts] = useState({});
    useEffect(() => {
        setDrafts(Object.fromEntries(tiers.map((t) => [t.id, { ...t }])));
    }, [tiers]);
    return (
        <div className="rounded-xl border border-brand-line bg-white p-6 space-y-4">
            <h3 className="font-heading font-bold">Pricing tiers</h3>
            {tiers.map((t) => {
                const d = drafts[t.id] || t;
                return (
                    <div key={t.id} className="grid sm:grid-cols-4 gap-3 items-end">
                        <div>
                            <label className="label-base">Name</label>
                            <input className="input-base" value={d.name || ""} onChange={(e) => setDrafts({ ...drafts, [t.id]: { ...d, name: e.target.value } })} />
                        </div>
                        <div>
                            <label className="label-base">Markup %</label>
                            <input type="number" className="input-base" value={d.markup_pct ?? 0} onChange={(e) => setDrafts({ ...drafts, [t.id]: { ...d, markup_pct: parseFloat(e.target.value || "0") } })} />
                        </div>
                        <div className="sm:col-span-1">
                            <label className="label-base">Description</label>
                            <input className="input-base" value={d.description || ""} onChange={(e) => setDrafts({ ...drafts, [t.id]: { ...d, description: e.target.value } })} />
                        </div>
                        <button data-testid={`tier-save-${t.id}`} onClick={() => onSave({ id: t.id, name: d.name, description: d.description, markup_pct: d.markup_pct, is_active: true })} className="pill btn-forest h-11 font-semibold">Save</button>
                    </div>
                );
            })}
        </div>
    );
};


const EmailTemplatesTab = ({ adminEmail }) => {
    const [templates, setTemplates] = useState([]);
    const [openKey, setOpenKey] = useState(null);
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);
    const [testTo, setTestTo] = useState(adminEmail || "");
    const [sending, setSending] = useState(false);

    const load = async () => {
        const res = await api.get("/admin/email-templates");
        setTemplates(res.data);
    };
    useEffect(() => { load(); }, []);
    useEffect(() => { if (adminEmail && !testTo) setTestTo(adminEmail); }, [adminEmail, testTo]);

    const startEdit = (t) => { setOpenKey(t.key); setDraft({ ...t }); };
    const cancelEdit = () => { setOpenKey(null); setDraft(null); };

    const save = async () => {
        if (!draft) return;
        setSaving(true);
        try {
            await api.patch(`/admin/email-templates/${draft.key}`, {
                subject: draft.subject,
                greeting: draft.greeting,
                body: draft.body,
                cta_label: draft.cta_label,
                cta_url_path: draft.cta_url_path,
                footer_note: draft.footer_note || "",
                sms_body: draft.sms_body !== undefined ? draft.sms_body : null,
            });
            toast.success("Template saved");
            await load();
            cancelEdit();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    const sendTest = async (key) => {
        if (!testTo) return toast.error("Enter a test email address");
        setSending(true);
        try {
            const res = await api.post(`/admin/email-templates/${key}/test`, {
                to: testTo,
                first_name: "Sam",
                event_name: "Spring Wellness Fair",
                body: "This is a preview of how this email will look to your users.",
            });
            if (res.data.stubbed) toast.message("Email stubbed (Resend not configured)");
            else toast.success(`Test email sent to ${testTo}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Send failed");
        } finally { setSending(false); }
    };

    return (
        <div className="space-y-4" data-testid="admin-email-templates">
            <div className="rounded-xl border border-brand-line bg-brand-bone/60 p-5">
                <h3 className="font-heading font-bold">Transactional emails</h3>
                <p className="text-sm text-brand-mute mt-1">Edit the subject, greeting, body, and call-to-action for any transactional email. Changes take effect immediately — no redeploy needed. Common placeholders you can use in <em>subject</em> / <em>greeting</em> / <em>body</em> / <em>CTA URL</em>: <code className="px-1 py-0.5 bg-white rounded text-xs">{"{first_name}"}</code>, <code className="px-1 py-0.5 bg-white rounded text-xs">{"{body}"}</code>, <code className="px-1 py-0.5 bg-white rounded text-xs">{"{event_name}"}</code>, <code className="px-1 py-0.5 bg-white rounded text-xs">{"{event_date}"}</code>, <code className="px-1 py-0.5 bg-white rounded text-xs">{"{vendor_name}"}</code>. The <strong>Claim invitation</strong> template also supports <code className="px-1 py-0.5 bg-white rounded text-xs">{"{vendor_company_name}"}</code> and <code className="px-1 py-0.5 bg-white rounded text-xs">{"{claim_link}"}</code> — use the latter inline as <code className="px-1 py-0.5 bg-white rounded text-xs">&lt;a href="{"{claim_link}"}"&gt;text&lt;/a&gt;</code> to embed a hyperlink anywhere in the body.</p>
                <div className="mt-4 flex flex-wrap items-end gap-3">
                    <div className="flex-1 min-w-[220px]">
                        <label className="label-base">Send tests to</label>
                        <input data-testid="email-tpl-test-to" className="input-base" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@yourdomain.com" />
                    </div>
                </div>
            </div>

            <div className="rounded-xl border border-brand-line bg-white overflow-hidden">
                {templates.map((t) => {
                    const isOpen = openKey === t.key;
                    return (
                        <div key={t.key} className="border-t border-brand-line first:border-t-0" data-testid={`email-tpl-row-${t.key}`}>
                            <div className="flex flex-wrap items-center gap-3 px-5 py-4">
                                <div className="flex-1 min-w-[200px]">
                                    <div className="font-semibold">{t.label}</div>
                                    <div className="text-xs text-brand-mute">{t.description}</div>
                                    <div className="text-[11px] text-brand-mute mt-1">key: <code>{t.key}</code></div>
                                </div>
                                <button data-testid={`email-tpl-test-${t.key}`} disabled={sending} onClick={() => sendTest(t.key)} className="pill px-4 py-2 text-xs font-semibold border border-brand-line bg-white hover:bg-brand-bone disabled:opacity-50">Send test</button>
                                <button data-testid={`email-tpl-edit-${t.key}`} onClick={() => isOpen ? cancelEdit() : startEdit(t)} className="pill btn-forest px-4 py-2 text-xs font-semibold">
                                    {isOpen ? "Close" : "Edit"}
                                </button>
                            </div>
                            {isOpen && draft && (
                                <div className="bg-brand-bone/40 border-t border-brand-line px-5 py-5 space-y-4">
                                    <div>
                                        <label className="label-base">Subject line</label>
                                        <input data-testid={`email-tpl-subject-${t.key}`} className="input-base" value={draft.subject || ""} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="label-base">Greeting</label>
                                        <input data-testid={`email-tpl-greeting-${t.key}`} className="input-base" value={draft.greeting || ""} onChange={(e) => setDraft({ ...draft, greeting: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="label-base">Body (HTML allowed — <code className="text-xs">{"{first_name}"}</code>, <code className="text-xs">{"{body}"}</code>, <code className="text-xs">{"{event_name}"}</code>)</label>
                                        <textarea data-testid={`email-tpl-body-${t.key}`} rows={5} className="input-base font-mono text-sm" value={draft.body || ""} onChange={(e) => setDraft({ ...draft, body: e.target.value })} />
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="label-base">CTA button label</label>
                                            <input data-testid={`email-tpl-cta-label-${t.key}`} className="input-base" value={draft.cta_label || ""} onChange={(e) => setDraft({ ...draft, cta_label: e.target.value })} placeholder="e.g., Confirm & pay" />
                                        </div>
                                        <div>
                                            <label className="label-base">CTA URL path <span className="text-brand-mute font-normal normal-case">(leave blank = dynamic link)</span></label>
                                            <input data-testid={`email-tpl-cta-url-${t.key}`} className="input-base" value={draft.cta_url_path || ""} onChange={(e) => setDraft({ ...draft, cta_url_path: e.target.value })} placeholder="leave blank for per-event dynamic link" />
                                            <p className="text-xs text-brand-mute mt-1.5 leading-relaxed">
                                                <strong>Leave blank</strong> to use the dynamic per-event link (e.g. <code className="bg-white px-1 rounded">/events/abc123</code> for a matched vendor email).<br />
                                                Or override with a static path like <code className="bg-white px-1 rounded">/marketplace</code>, or a templated one like <code className="bg-white px-1 rounded">{"{link}"}</code> (= the same dynamic link).
                                            </p>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="label-base">Footer note (optional)</label>
                                        <input data-testid={`email-tpl-footer-${t.key}`} className="input-base" value={draft.footer_note || ""} onChange={(e) => setDraft({ ...draft, footer_note: e.target.value })} placeholder="e.g., Your spot is held until payment is received." />
                                    </div>
                                    {/* SMS body — only meaningful for templates in SMS_ENABLED_KEYS.
                                        We surface the field whenever the doc already carries an
                                        sms_body (or could), and show a disabled-state hint for the
                                        rest so admins understand why some templates can't text. */}
                                    {(t.sms_body !== undefined || ["application_approved","application_declined","event_invitation","broadcast","new_message","waitlist_joined"].includes(t.key)) ? (
                                        <div>
                                            <label className="label-base flex items-center justify-between">
                                                <span>SMS body <span className="text-brand-mute font-normal normal-case">(160-char target · plain text · sent to opted-in vendors)</span></span>
                                                <span className={`text-[11px] tabular-nums ${(draft.sms_body || "").length > 160 ? "text-brand-coral" : "text-brand-mute"}`}>
                                                    {(draft.sms_body || "").length}/160
                                                </span>
                                            </label>
                                            <textarea
                                                data-testid={`email-tpl-sms-${t.key}`}
                                                rows={2}
                                                className="input-base font-mono text-sm"
                                                value={draft.sms_body || ""}
                                                onChange={(e) => setDraft({ ...draft, sms_body: e.target.value })}
                                                placeholder="HealthFairX: …"
                                            />
                                            <p className="text-xs text-brand-mute mt-1.5 leading-relaxed">
                                                Available placeholders: <code className="bg-white px-1 rounded text-xs">{"{event_name}"}</code>, <code className="bg-white px-1 rounded text-xs">{"{event_date}"}</code>, <code className="bg-white px-1 rounded text-xs">{"{app_url}"}</code>, <code className="bg-white px-1 rounded text-xs">{"{first_name}"}</code>. Leave blank to disable SMS for this trigger (email still sends). Reply HELP/STOP is implicit per the A2P consent disclosure.
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="text-xs text-brand-mute italic">SMS not enabled for this trigger (not on the A2P transactional whitelist).</div>
                                    )}
                                    <div className="flex justify-end gap-2 pt-1">
                                        <button onClick={cancelEdit} className="pill px-4 py-2 text-sm font-semibold border border-brand-line bg-white">Cancel</button>
                                        <button data-testid={`email-tpl-save-${t.key}`} disabled={saving} onClick={save} className="pill btn-coral px-4 py-2 text-sm font-semibold disabled:opacity-50">{saving ? "Saving…" : "Save changes"}</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
                {templates.length === 0 && <div className="p-8 text-center text-brand-mute">No templates yet — refresh in a moment.</div>}
            </div>
        </div>
    );
};

const SiteAssetsTab = () => {
    const [assets, setAssets] = useState([]);
    const [drafts, setDrafts] = useState({});  // { key: url }
    const [saving, setSaving] = useState({});
    const fileRefs = React.useRef({});

    const load = async () => {
        const res = await api.get("/admin/site-assets");
        setAssets(res.data);
        setDrafts(Object.fromEntries(res.data.map((a) => [a.key, a.url || ""])));
    };
    useEffect(() => { load(); }, []);

    const save = async (key) => {
        const url = (drafts[key] || "").trim();
        if (!url) return toast.error("URL required");
        setSaving((s) => ({ ...s, [key]: true }));
        try {
            await api.patch(`/admin/site-assets/${key}`, { url });
            toast.success("Saved");
            await load();
            await refreshSiteAssets();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally { setSaving((s) => ({ ...s, [key]: false })); }
    };

    const reset = async (key) => {
        if (!window.confirm("Reset this image to the built-in default?")) return;
        setSaving((s) => ({ ...s, [key]: true }));
        try {
            await api.post(`/admin/site-assets/${key}/reset`);
            toast.success("Reset to default");
            await load();
            await refreshSiteAssets();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Reset failed");
        } finally { setSaving((s) => ({ ...s, [key]: false })); }
    };

    const upload = async (key, file) => {
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) return toast.error("Max 5MB");
        setSaving((s) => ({ ...s, [key]: true }));
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await api.patch(`/admin/site-assets/${key}`, { url: res.data.path });
            toast.success("Uploaded & saved");
            await load();
            await refreshSiteAssets();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setSaving((s) => ({ ...s, [key]: false }));
            if (fileRefs.current[key]) fileRefs.current[key].value = "";
        }
    };

    return (
        <div className="space-y-4" data-testid="admin-site-assets">
            <div className="rounded-xl border border-brand-line bg-brand-bone/60 p-5">
                <h3 className="font-heading font-bold">Site images & branding</h3>
                <p className="text-sm text-brand-mute mt-1">Replace any image on the public site. Paste an external URL (Unsplash, Pexels, your CDN) or upload from your computer. The new image goes live everywhere immediately — no redeploy.</p>
            </div>

            <div className="space-y-3">
                {assets.map((a) => {
                    const draft = drafts[a.key] ?? "";
                    const isLogo = a.key === "brand_logo";
                    const previewUrl = imageUrl(draft || a.default_url);
                    return (
                        <div key={a.key} className="rounded-xl border border-brand-line bg-white overflow-hidden" data-testid={`asset-row-${a.key}`}>
                            <div className="grid sm:grid-cols-[auto,1fr] gap-5 p-5">
                                <div className={`${isLogo ? "h-24 w-24" : "h-28 w-44"} rounded-xl overflow-hidden border border-brand-line bg-brand-bone shrink-0`}>
                                    {previewUrl && <img src={previewUrl} alt="" className={isLogo ? "w-full h-full object-contain" : "w-full h-full object-cover"} />}
                                </div>
                                <div className="min-w-0">
                                    <div className="font-semibold">{a.label}</div>
                                    <div className="text-xs text-brand-mute mt-0.5">{a.description}</div>
                                    <div className="text-[11px] text-brand-mute mt-1">key: <code>{a.key}</code></div>

                                    <div className="mt-3 flex flex-wrap items-center gap-2">
                                        <input
                                            data-testid={`asset-url-${a.key}`}
                                            className="input-base flex-1 min-w-[260px]"
                                            value={draft}
                                            onChange={(e) => setDrafts((d) => ({ ...d, [a.key]: e.target.value }))}
                                            placeholder="https://… or upload below"
                                        />
                                        <button data-testid={`asset-save-${a.key}`} disabled={saving[a.key]} onClick={() => save(a.key)} className="pill btn-coral px-4 py-2 text-sm font-semibold disabled:opacity-50">{saving[a.key] ? "Saving…" : "Save URL"}</button>
                                        <button data-testid={`asset-upload-${a.key}`} disabled={saving[a.key]} onClick={() => fileRefs.current[a.key]?.click()} className="pill px-4 py-2 text-sm font-semibold border border-brand-line bg-white hover:bg-brand-bone disabled:opacity-50">Upload…</button>
                                        <button data-testid={`asset-reset-${a.key}`} disabled={saving[a.key]} onClick={() => reset(a.key)} className="pill px-4 py-2 text-sm font-semibold text-brand-coral hover:bg-brand-coral/5 disabled:opacity-50">Reset</button>
                                        <input
                                            ref={(el) => { fileRefs.current[a.key] = el; }}
                                            type="file"
                                            accept="image/jpeg,image/png,image/webp,image/gif"
                                            className="hidden"
                                            onChange={(e) => upload(a.key, e.target.files?.[0])}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
                {assets.length === 0 && <div className="p-8 text-center text-brand-mute rounded-xl border border-brand-line bg-white">Loading…</div>}
            </div>
        </div>
    );
};


const SiteContentTab = () => {
    const [items, setItems] = useState([]);
    const [drafts, setDrafts] = useState({});
    const [saving, setSaving] = useState({});

    const load = async () => {
        const res = await api.get("/admin/site-content");
        setItems(res.data);
        setDrafts(Object.fromEntries(res.data.map((c) => [c.key, c.value])));
    };
    useEffect(() => { load(); }, []);

    const save = async (key) => {
        const value = (drafts[key] ?? "").trim();
        if (!value) return toast.error("Value required");
        setSaving((s) => ({ ...s, [key]: true }));
        try {
            await api.patch(`/admin/site-content/${key}`, { value });
            toast.success("Saved");
            await load();
            await refreshSiteContent();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally { setSaving((s) => ({ ...s, [key]: false })); }
    };

    const reset = async (key) => {
        if (!window.confirm("Reset this value to the built-in default?")) return;
        setSaving((s) => ({ ...s, [key]: true }));
        try {
            await api.post(`/admin/site-content/${key}/reset`);
            toast.success("Reset");
            await load();
            await refreshSiteContent();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Reset failed");
        } finally { setSaving((s) => ({ ...s, [key]: false })); }
    };

    // Group by stat number for nice layout
    const groups = [1, 2, 3].map((n) => ({
        n,
        value: items.find((c) => c.key === `landing_stat_${n}_value`),
        label: items.find((c) => c.key === `landing_stat_${n}_label`),
    }));

    // Group the four "How it works" steps (title + body per step).
    const hiwSteps = [1, 2, 3, 4].map((n) => ({
        n,
        title: items.find((c) => c.key === `hiw_step_${n}_title`),
        body: items.find((c) => c.key === `hiw_step_${n}_body`),
    }));

    return (
        <div className="space-y-4" data-testid="admin-site-content">
            <div className="rounded-xl border border-brand-line bg-brand-bone/60 p-5">
                <h3 className="font-heading font-bold">Homepage stats</h3>
                <p className="text-sm text-brand-mute mt-1">The three stat blurbs in your homepage hero. Edit the bold value (large forest-green text) and the small label (uppercase caption) independently. Changes go live instantly.</p>
            </div>

            <div className="space-y-3">
                {groups.map(({ n, value, label }) => (
                    <div key={n} className="rounded-xl border border-brand-line bg-white p-5" data-testid={`stat-row-${n}`}>
                        <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
                            <div className="font-semibold">Stat {n}</div>
                            {value && label && (
                                <div className="rounded-lg bg-brand-bone px-4 py-2.5">
                                    <div className="font-heading text-2xl font-extrabold text-brand-forest leading-none">{drafts[value.key] || value.value}</div>
                                    <div className="text-[10px] uppercase tracking-wider text-brand-mute mt-1.5">{drafts[label.key] || label.value}</div>
                                </div>
                            )}
                        </div>

                        <div className="grid sm:grid-cols-2 gap-4">
                            {value && (
                                <div>
                                    <label className="label-base">Bold value <span className="text-brand-mute font-normal normal-case">(big number / phrase)</span></label>
                                    <div className="flex gap-2 mt-1">
                                        <input data-testid={`stat-value-${n}`} className="input-base font-heading font-bold text-lg" value={drafts[value.key] ?? ""} onChange={(e) => setDrafts((d) => ({ ...d, [value.key]: e.target.value }))} maxLength={30} placeholder="e.g. 40+" />
                                        <button data-testid={`stat-value-save-${n}`} disabled={saving[value.key]} onClick={() => save(value.key)} className="pill btn-coral px-4 py-2 text-sm font-semibold disabled:opacity-50">{saving[value.key] ? "…" : "Save"}</button>
                                        <button data-testid={`stat-value-reset-${n}`} disabled={saving[value.key]} onClick={() => reset(value.key)} className="pill px-3 py-2 text-xs font-semibold text-brand-coral hover:bg-brand-coral/5 disabled:opacity-50">Reset</button>
                                    </div>
                                </div>
                            )}
                            {label && (
                                <div>
                                    <label className="label-base">Small label <span className="text-brand-mute font-normal normal-case">(caption underneath)</span></label>
                                    <div className="flex gap-2 mt-1">
                                        <input data-testid={`stat-label-${n}`} className="input-base text-sm" value={drafts[label.key] ?? ""} onChange={(e) => setDrafts((d) => ({ ...d, [label.key]: e.target.value }))} maxLength={50} placeholder="e.g. Vendor categories" />
                                        <button data-testid={`stat-label-save-${n}`} disabled={saving[label.key]} onClick={() => save(label.key)} className="pill btn-coral px-4 py-2 text-sm font-semibold disabled:opacity-50">{saving[label.key] ? "…" : "Save"}</button>
                                        <button data-testid={`stat-label-reset-${n}`} disabled={saving[label.key]} onClick={() => reset(label.key)} className="pill px-3 py-2 text-xs font-semibold text-brand-coral hover:bg-brand-coral/5 disabled:opacity-50">Reset</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                {items.length === 0 && <div className="p-8 text-center text-brand-mute rounded-xl border border-brand-line bg-white">Loading…</div>}
            </div>

            <div className="rounded-xl border border-brand-line bg-brand-bone/60 p-5 mt-8" data-testid="hiw-section-header">
                <h3 className="font-heading font-bold">How it works — four-step process</h3>
                <p className="text-sm text-brand-mute mt-1">The four-step explainer in the "Plan your health fair" section of the homepage. Edit the title and subtext for each step. Changes go live instantly.</p>
            </div>

            <div className="space-y-3">
                {hiwSteps.map(({ n, title, body }) => {
                    if (!title || !body) return null;
                    return (
                        <div key={n} className="rounded-xl border border-brand-line bg-white p-5" data-testid={`hiw-row-${n}`}>
                            <div className="flex items-baseline justify-between mb-3">
                                <div className="font-semibold">Step {n}</div>
                                <div className="text-[10px] font-bold text-brand-coral uppercase tracking-wider">STEP {n}</div>
                            </div>
                            <div className="space-y-3">
                                <div>
                                    <label className="label-base">Title</label>
                                    <div className="flex gap-2 mt-1">
                                        <input data-testid={`hiw-title-${n}`} className="input-base font-heading font-bold" value={drafts[title.key] ?? ""} onChange={(e) => setDrafts((d) => ({ ...d, [title.key]: e.target.value }))} maxLength={60} placeholder="e.g. Tell us about your event" />
                                        <button data-testid={`hiw-title-save-${n}`} disabled={saving[title.key]} onClick={() => save(title.key)} className="pill btn-coral px-4 py-2 text-sm font-semibold disabled:opacity-50">{saving[title.key] ? "…" : "Save"}</button>
                                        <button data-testid={`hiw-title-reset-${n}`} disabled={saving[title.key]} onClick={() => reset(title.key)} className="pill px-3 py-2 text-xs font-semibold text-brand-coral hover:bg-brand-coral/5 disabled:opacity-50">Reset</button>
                                    </div>
                                </div>
                                <div>
                                    <label className="label-base">Subtext</label>
                                    <div className="flex gap-2 mt-1">
                                        <textarea data-testid={`hiw-body-${n}`} rows={2} className="input-base !h-auto py-3 text-sm flex-1" value={drafts[body.key] ?? ""} onChange={(e) => setDrafts((d) => ({ ...d, [body.key]: e.target.value }))} maxLength={300} placeholder="Short description that appears under the title" />
                                        <div className="flex flex-col gap-1 shrink-0">
                                            <button data-testid={`hiw-body-save-${n}`} disabled={saving[body.key]} onClick={() => save(body.key)} className="pill btn-coral px-4 py-2 text-sm font-semibold disabled:opacity-50">{saving[body.key] ? "…" : "Save"}</button>
                                            <button data-testid={`hiw-body-reset-${n}`} disabled={saving[body.key]} onClick={() => reset(body.key)} className="pill px-3 py-1.5 text-xs font-semibold text-brand-coral hover:bg-brand-coral/5 disabled:opacity-50">Reset</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};



// ----------------------------------------------------------------------------- 
// Admin support edit modals — let support fix any field on a user / event /
// vendor profile without DB access. Each modal POSTs only changed fields to a
// dedicated admin PATCH endpoint.
// ----------------------------------------------------------------------------- 
const ModalShell = ({ title, subtitle, onClose, children, testId, wide = false }) => (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid={testId}>
        <div className={`bg-white rounded-2xl ${wide ? "max-w-3xl" : "max-w-xl"} w-full p-8 max-h-[90vh] overflow-auto`}>
            <div className="flex items-start justify-between">
                <div>
                    <h2 className="font-heading text-2xl font-extrabold">{title}</h2>
                    {subtitle && <p className="text-sm text-brand-mute mt-1">{subtitle}</p>}
                </div>
                <button onClick={onClose} className="rounded-full p-2 hover:bg-brand-bone" aria-label="Close" data-testid="modal-close">
                    <X size={18} />
                </button>
            </div>
            <div className="mt-6">{children}</div>
        </div>
    </div>
);

// ----------------------------------------------------------------------------- 
// AdminEventDetailModal
// Deep-dive on any event: shows applications + vendor pitch messages,
// confirmed vendors, payments, pricing per category, and host↔vendor
// conversations. This is the admin's parity view — everything the host sees
// on their own event dashboard, plus payment ledger + a manual "resend
// vendor_paid email" escape hatch for edge cases where the original
// notification was missed.
// ----------------------------------------------------------------------------- 
const AdminEventDetailModal = ({ eventId, onClose }) => {
    const [event, setEvent] = useState(null);
    const [apps, setApps] = useState([]);
    const [pays, setPays] = useState([]);
    const [pricing, setPricing] = useState(null);
    const [convs, setConvs] = useState([]);
    const [subTab, setSubTab] = useState("applications");
    const [busy, setBusy] = useState(false);
    // Comp modal — set to the application object when the admin clicks
    // "Comp"; the modal collects an optional reason before firing the
    // POST /admin/events/{id}/applications/{app_id}/comp endpoint.
    const [compFor, setCompFor] = useState(null);

    const load = async () => {
        setBusy(true);
        try {
            const [e, a, p, pr, c] = await Promise.all([
                api.get(`/admin/events/${eventId}`),
                api.get(`/admin/events/${eventId}/applications`),
                api.get(`/admin/events/${eventId}/payments`),
                api.get(`/admin/events/${eventId}/pricing`),
                api.get(`/admin/events/${eventId}/conversations`),
            ]);
            setEvent(e.data);
            setApps(a.data);
            setPays(p.data);
            setPricing(pr.data);
            setConvs(c.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not load event details");
        } finally {
            setBusy(false);
        }
    };
    useEffect(() => { load(); }, [eventId]);// eslint-disable-line react-hooks/exhaustive-deps

    const resendPaidEmail = async (paymentId) => {
        try {
            await api.post(`/admin/payments/${paymentId}/resend-host-notification`);
            toast.success("Host notification re-sent");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Resend failed");
        }
    };

    const submitComp = async (reason) => {
        if (!compFor) return;
        try {
            await api.post(`/admin/events/${eventId}/applications/${compFor.id}/comp`, { reason });
            toast.success(`${compFor.vendor?.business_name || "Vendor"} confirmed — no payment required`);
            setCompFor(null);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Comp failed");
        }
    };

    const SUB_TABS = [
        { id: "applications", label: `Applications (${apps.length})` },
        { id: "payments", label: `Payments (${pays.length})` },
        { id: "pricing", label: "Pricing" },
        { id: "conversations", label: `Messages (${convs.length})` },
    ];

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="admin-event-detail-modal">
            <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-auto">
                <div className="sticky top-0 bg-white border-b border-brand-line px-8 py-5 flex items-start justify-between z-10">
                    <div>
                        <h2 className="font-heading text-2xl font-extrabold" data-testid="admin-event-detail-title">
                            {event?.name || "Event details"}
                        </h2>
                        <p className="text-sm text-brand-mute mt-1">
                            {event ? (
                                <>
                                    {fmtDate(event.event_date)} · {event.host?.company_name || "—"} ·{" "}
                                    <span className="chip">{(event.status || "").replace("_", " ")}</span>
                                </>
                            ) : "Loading…"}
                        </p>
                    </div>
                    <button onClick={onClose} className="rounded-full p-2 hover:bg-brand-bone" aria-label="Close" data-testid="admin-event-detail-close">
                        <X size={18} />
                    </button>
                </div>

                <div className="px-8 pt-6">
                    <div className="flex flex-wrap gap-2 border-b border-brand-line">
                        {SUB_TABS.map((t) => (
                            <button
                                key={t.id}
                                data-testid={`admin-event-tab-${t.id}`}
                                onClick={() => setSubTab(t.id)}
                                className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${subTab === t.id ? "border-brand-forest text-brand-forest" : "border-transparent text-brand-mute hover:text-brand-ink"}`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="p-8">
                    {busy && !event && <p className="text-brand-mute">Loading…</p>}

                    {subTab === "applications" && (
                        <div className="space-y-3" data-testid="admin-event-applications-list">
                            {apps.length === 0 && <p className="text-brand-mute">No applications yet.</p>}
                            {apps.map((a) => (
                                <div key={a.id} className="border border-brand-line rounded-lg p-4" data-testid={`admin-app-${a.id}`}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="font-semibold">
                                                {a.vendor?.business_name || a.vendor_contact?.full_name || a.vendor_contact?.email || "Unknown vendor"}
                                            </div>
                                            <div className="text-xs text-brand-mute mt-0.5">
                                                {a.vendor_contact?.email}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 whitespace-nowrap">
                                            {a.is_comped && (
                                                <span className="chip bg-brand-forest/10 text-brand-forest border-brand-forest/30" data-testid={`comped-badge-${a.id}`}>Comped</span>
                                            )}
                                            <span className="chip">{(a.status || "").replace(/_/g, " ")}</span>
                                            {(a.status === "interested" || a.status === "approved_pending_payment") && !a.is_comped && (
                                                <button
                                                    data-testid={`comp-application-${a.id}`}
                                                    onClick={() => setCompFor(a)}
                                                    className="pill px-3 py-1.5 text-xs font-semibold border border-brand-forest/40 text-brand-forest bg-white hover:bg-brand-forest/5 inline-flex items-center gap-1"
                                                    title="Confirm this vendor without collecting payment"
                                                >
                                                    <Gift size={12} /> Comp
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    {a.pitch_message && (
                                        <div className="mt-3 bg-brand-bone/60 rounded-md p-3 text-sm">
                                            <div className="text-xs text-brand-mute mb-1 font-semibold uppercase tracking-wide">Pitch message</div>
                                            <p className="whitespace-pre-wrap">{a.pitch_message}</p>
                                        </div>
                                    )}
                                    {a.is_comped && a.comp_reason && (
                                        <div className="mt-3 text-xs text-brand-mute" data-testid={`comp-reason-${a.id}`}>
                                            <strong>Comp reason:</strong> {a.comp_reason}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {subTab === "payments" && (
                        <div className="space-y-3" data-testid="admin-event-payments-list">
                            {pays.length === 0 && <p className="text-brand-mute">No successful payments yet.</p>}
                            {pays.map((p) => (
                                <div key={p.id} className="border border-brand-line rounded-lg p-4 flex items-start justify-between gap-3" data-testid={`admin-payment-${p.id}`}>
                                    <div className="min-w-0">
                                        <div className="font-semibold flex items-center gap-2">
                                            {p.vendor_business_name || p.vendor_contact?.full_name || p.vendor_contact?.email || "Unknown vendor"}
                                            {p.status === "comped" && (
                                                <span className="chip bg-brand-forest/10 text-brand-forest border-brand-forest/30 text-[10px]" data-testid={`payment-comped-${p.id}`}>Comped</span>
                                            )}
                                        </div>
                                        <div className="text-xs text-brand-mute mt-0.5">
                                            {p.vendor_contact?.email}
                                        </div>
                                        <div className="text-sm mt-2">
                                            <strong>{p.status === "comped" ? "Comped ($0)" : dollars(p.amount_cents)}</strong> · {new Date(p.created_at).toLocaleString()}
                                        </div>
                                        {p.status === "comped" && p.comp_reason && (
                                            <div className="text-xs text-brand-mute mt-1">
                                                <strong>Reason:</strong> {p.comp_reason}
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        data-testid={`resend-paid-${p.id}`}
                                        onClick={() => resendPaidEmail(p.id)}
                                        className="pill px-3 py-1.5 text-xs font-semibold border border-brand-forest/40 text-brand-forest bg-white hover:bg-brand-forest/5 inline-flex items-center gap-1 whitespace-nowrap"
                                        title="Resend the confirmation email to the host"
                                    >
                                        <RefreshCw size={12} /> Resend host email
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {subTab === "pricing" && (
                        <div data-testid="admin-event-pricing">
                            {pricing ? (
                                <>
                                    <div className="text-sm text-brand-mute mb-3">
                                        Tier: <strong>{pricing.tier_name || "—"}</strong>{" "}
                                        · Markup: <strong>{pricing.tier_markup_pct}%</strong>
                                    </div>
                                    <table className="w-full text-sm border border-brand-line rounded-lg overflow-hidden">
                                        <thead className="bg-brand-bone text-brand-mute">
                                            <tr>
                                                <th className="text-left px-4 py-2">Category</th>
                                                <th className="text-right px-4 py-2">Baseline</th>
                                                <th className="text-right px-4 py-2">Final (vendor pays)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pricing.rows.map((r) => (
                                                <tr key={r.category_id} className="border-t border-brand-line">
                                                    <td className="px-4 py-2 font-semibold">{r.category_name}</td>
                                                    <td className="px-4 py-2 text-right">{dollars(r.baseline_price_cents)}</td>
                                                    <td className="px-4 py-2 text-right font-semibold">{dollars(r.final_price_cents)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {pricing.rows.length === 0 && <p className="text-brand-mute mt-3">No pricing set for this event yet.</p>}
                                </>
                            ) : (
                                <p className="text-brand-mute">Loading…</p>
                            )}
                        </div>
                    )}

                    {subTab === "conversations" && (
                        <div className="space-y-4" data-testid="admin-event-conversations-list">
                            {convs.length === 0 && <p className="text-brand-mute">No conversations for this event yet.</p>}
                            {convs.map((c) => (
                                <div key={c.id} className="border border-brand-line rounded-lg p-4" data-testid={`admin-conv-${c.id}`}>
                                    <div className="flex items-center justify-between text-sm">
                                        <div>
                                            <strong>{c.host?.full_name || c.host?.email || "Host"}</strong>
                                            {" ↔ "}
                                            <strong>{c.vendor?.business_name || c.vendor?.full_name || c.vendor?.email || "Vendor"}</strong>
                                        </div>
                                        <span className="text-xs text-brand-mute">
                                            {c.last_message_at ? new Date(c.last_message_at).toLocaleString() : ""}
                                        </span>
                                    </div>
                                    <div className="mt-3 space-y-2">
                                        {(c.messages || []).slice(-5).map((m) => (
                                            <div key={m.id} className="bg-brand-bone/60 rounded-md p-2 text-sm">
                                                <div className="text-xs text-brand-mute font-semibold uppercase tracking-wide mb-0.5">
                                                    {m.sender_role === "host" ? "Host" : "Vendor"} · {new Date(m.created_at).toLocaleString()}
                                                </div>
                                                <p className="whitespace-pre-wrap">{m.body}</p>
                                            </div>
                                        ))}
                                        {(c.messages || []).length === 0 && (
                                            <p className="text-brand-mute text-sm">No messages yet.</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            {compFor && (
                <CompReasonModal
                    application={compFor}
                    onClose={() => setCompFor(null)}
                    onSubmit={submitComp}
                />
            )}
        </div>
    );
};


// Small confirmation modal for the "Comp vendor" action. Collects an
// optional reason (audit trail) and fires the parent's submit handler.
// Kept lightweight because the underlying decision is reversible via the
// admin edit application endpoint if needed.
const CompReasonModal = ({ application, onClose, onSubmit }) => {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const vendorName =
        application.vendor?.business_name ||
        application.vendor_contact?.full_name ||
        application.vendor_contact?.email ||
        "this vendor";
    const submit = async () => {
        setBusy(true);
        try { await onSubmit(reason.trim()); } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" data-testid="comp-reason-modal">
            <div className="bg-white rounded-2xl max-w-md w-full p-6">
                <div className="flex items-start justify-between gap-3">
                    <h3 className="font-heading text-xl font-extrabold">Comp {vendorName}?</h3>
                    <button onClick={onClose} className="rounded-full p-1.5 hover:bg-brand-bone" aria-label="Close" data-testid="comp-modal-close">
                        <X size={16} />
                    </button>
                </div>
                <p className="text-sm text-brand-mute mt-2">
                    They'll be confirmed for the event without paying. The vendor will get a confirmation email; the host will get their standard "vendor confirmed" notification.
                </p>
                <label className="label-base mt-4 block">Reason (optional)</label>
                <textarea
                    data-testid="comp-reason-input"
                    className="input-base w-full min-h-[90px]"
                    placeholder="e.g. Sponsor swap · VIP · payment issue"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    maxLength={500}
                />
                <div className="flex items-center justify-end gap-2 mt-5">
                    <button onClick={onClose} className="pill px-4 py-2 text-sm font-semibold border border-brand-line bg-white hover:bg-brand-bone">Cancel</button>
                    <button
                        data-testid="comp-confirm-btn"
                        onClick={submit}
                        disabled={busy}
                        className="pill btn-coral px-4 py-2 text-sm font-semibold disabled:opacity-60"
                    >
                        {busy ? "Confirming…" : "Confirm & comp"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const EditUserModal = ({ user, onClose, onSaved }) => {
    const [form, setForm] = useState({
        email: user.email || "",
        full_name: user.full_name || "",
        role: user.role,
        is_active: user.is_active !== false,
    });
    const [busy, setBusy] = useState(false);
    const [testFlag, setTestFlag] = useState(!!user.is_test_user);
    const toggleTest = async () => {
        const next = !testFlag;
        setTestFlag(next);
        try {
            await api.post(`/admin/users/${user.id}/test-flag`, { is_test_user: next });
            toast.success(`Marked as ${next ? "test" : "real"} user`);
        } catch (err) { toast.error(err?.response?.data?.detail || "Toggle failed"); setTestFlag(!next); }
    };
    const save = async () => {
        setBusy(true);
        try {
            const payload = {};
            if (form.email !== user.email) payload.email = form.email.trim().toLowerCase();
            if (form.full_name !== user.full_name) payload.full_name = form.full_name.trim();
            if (form.role !== user.role) payload.role = form.role;
            if (form.is_active !== (user.is_active !== false)) payload.is_active = form.is_active;
            if (Object.keys(payload).length === 0) { toast("Nothing changed"); setBusy(false); return; }
            await api.patch(`/admin/users/${user.id}`, payload);
            toast.success(`Updated ${form.email}` + (payload.email ? " (notification emails sent)" : ""));
            onSaved();
        } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
        finally { setBusy(false); }
    };
    return (
        <ModalShell title="Edit user" subtitle={`User ID: ${user.id}`} onClose={onClose} testId="edit-user-modal">
            <div className="space-y-4">
                <div>
                    <label className="label-base">Email</label>
                    <input data-testid="edit-user-email" type="email" className="input-base" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    <p className="text-xs text-brand-mute mt-1">Both the old and new addresses will receive a notification.</p>
                </div>
                <div>
                    <label className="label-base">Full name</label>
                    <input data-testid="edit-user-name" className="input-base" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="label-base">Role</label>
                        <select data-testid="edit-user-role" className="input-base" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                            <option value="vendor">vendor</option>
                            <option value="host_company">host_company</option>
                            <option value="admin">admin</option>
                        </select>
                    </div>
                    <div>
                        <label className="label-base">Active</label>
                        <select data-testid="edit-user-active" className="input-base" value={form.is_active ? "1" : "0"} onChange={(e) => setForm({ ...form, is_active: e.target.value === "1" })}>
                            <option value="1">Active</option>
                            <option value="0">Disabled</option>
                        </select>
                    </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                    <button onClick={onClose} className="pill px-5 py-3 font-semibold border border-brand-line bg-white">Cancel</button>
                    <button data-testid="edit-user-save" disabled={busy} onClick={save} className="pill btn-coral px-5 py-3 font-semibold disabled:opacity-50">{busy ? "Saving…" : "Save changes"}</button>
                </div>
                <div className="mt-6 pt-5 border-t border-brand-line">
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <div className="font-semibold">Test account</div>
                            <div className="text-xs text-brand-mute">When ON, this user never receives emails. Used for Emergent QA accounts.</div>
                        </div>
                        <button
                            data-testid="edit-user-test-flag"
                            onClick={toggleTest}
                            className={`pill px-4 py-2 text-xs font-semibold border ${testFlag ? "bg-brand-coral text-white border-brand-coral" : "bg-white text-brand-ink border-brand-line"}`}
                        >
                            {testFlag ? "Yes — test user" : "No — real user"}
                        </button>
                    </div>
                </div>
            </div>
        </ModalShell>
    );
};

const EditEventModal = ({ event, cats, hosts, onClose, onSaved }) => {
    const [form, setForm] = useState({
        name: event.name || "",
        description: event.description || "",
        event_date: event.event_date || "",
        start_time: event.start_time || "",
        end_time: event.end_time || "",
        location: event.location || "",
        city: event.city || "",
        state: event.state || "",
        parking_instructions: event.parking_instructions || "",
        setup_instructions: event.setup_instructions || "",
        total_booth_capacity: event.total_booth_capacity || 10,
        total_employees: event.total_employees ?? "",
        images: event.images || [],
        status: event.status || "draft",
        host_id: event.host_id || "",
        event_slug: event.event_slug || "",
        // Seed unified category controls from category_controls (source of truth)
        // or fall back to legacy desired_categories for grandfathered events.
        category_controls: (event.category_controls && event.category_controls.length)
            ? event.category_controls.map((r) => ({
                category_id: r.category_id,
                max_count: r.max_count || 1,
                desired_count: r.desired_count || r.max_count || 1,
                waitlist_enabled: r.waitlist_enabled !== false,
                requires_approval_after_cap: r.requires_approval_after_cap !== false,
            }))
            : (event.desired_categories || []).map((cid) => ({
                category_id: cid, max_count: 1, desired_count: 1,
                waitlist_enabled: true, requires_approval_after_cap: true,
            })),
        event_info: event.event_info || {},
    });
    const [busy, setBusy] = useState(false);
    const toggleCat = (id) => {
        setForm((f) => {
            const has = f.category_controls.some((r) => r.category_id === id);
            return {
                ...f,
                category_controls: has
                    ? f.category_controls.filter((r) => r.category_id !== id)
                    : [...f.category_controls, { category_id: id, max_count: 1, desired_count: 1, waitlist_enabled: true, requires_approval_after_cap: true }],
            };
        });
    };
    const updateRow = (cid, patch) => setForm((f) => ({
        ...f,
        category_controls: f.category_controls.map((r) => r.category_id === cid ? { ...r, ...patch } : r),
    }));
    const save = async () => {
        setBusy(true);
        try {
            const payload = { ...form };
            if (payload.total_employees === "") payload.total_employees = null;
            else payload.total_employees = parseInt(payload.total_employees, 10);
            payload.total_booth_capacity = parseInt(payload.total_booth_capacity, 10);
            // Slug: send only if the admin changed it from the seeded value
            // (avoids needlessly thrashing the field and rebuilding indexes).
            if ((payload.event_slug || "") === (event.event_slug || "")) {
                delete payload.event_slug;
            }
            // Drop unchanged primitive fields to keep audit clean
            Object.keys(payload).forEach((k) => {
                if (payload[k] === null || payload[k] === undefined) delete payload[k];
            });
            await api.patch(`/admin/events/${event.id}`, payload);
            toast.success("Event updated");
            onSaved();
        } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
        finally { setBusy(false); }
    };
    return (
        <ModalShell wide title="Edit event" subtitle={event.name} onClose={onClose} testId="edit-event-modal">
            <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                        <label className="label-base">Name</label>
                        <input data-testid="edit-event-name" className="input-base" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    </div>
                    <div className="sm:col-span-2">
                        <label className="label-base">URL slug</label>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-brand-mute font-mono shrink-0">/events/</span>
                            <input
                                data-testid="edit-event-slug"
                                className="input-base font-mono"
                                value={form.event_slug}
                                onChange={(e) => setForm({ ...form, event_slug: e.target.value })}
                                placeholder="acme-wellness-fair"
                            />
                        </div>
                        <p className="text-xs text-brand-mute mt-1">Lowercase letters, numbers, and dashes. Must be unique across all events. Don't change this on a published event unless you really need to — links shared elsewhere won't auto-redirect.</p>
                    </div>
                    <div className="sm:col-span-2">
                        <label className="label-base">Description</label>
                        <textarea data-testid="edit-event-desc" rows={3} className="input-base !h-auto py-3" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                    </div>
                    <div>
                        <label className="label-base">Date</label>
                        <input data-testid="edit-event-date" type="date" className="input-base" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} />
                    </div>
                    <div>
                        <label className="label-base">Booth capacity</label>
                        <input data-testid="edit-event-capacity" type="number" min={1} className="input-base" value={form.total_booth_capacity} onChange={(e) => setForm({ ...form, total_booth_capacity: e.target.value })} />
                    </div>
                    <div>
                        <label className="label-base">Start time</label>
                        <input data-testid="edit-event-start" className="input-base" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} placeholder="10:00" />
                    </div>
                    <div>
                        <label className="label-base">End time</label>
                        <input data-testid="edit-event-end" className="input-base" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} placeholder="15:00" />
                    </div>
                    <div className="sm:col-span-2">
                        <label className="label-base">Location (street address)</label>
                        <input data-testid="edit-event-location" className="input-base" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                    </div>
                    <div>
                        <label className="label-base">City</label>
                        <input data-testid="edit-event-city" className="input-base" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                    </div>
                    <div>
                        <label className="label-base">State</label>
                        <input data-testid="edit-event-state" className="input-base" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
                    </div>
                    <div>
                        <label className="label-base">Total employees at location</label>
                        <input data-testid="edit-event-employees" type="number" min={0} className="input-base" value={form.total_employees} onChange={(e) => setForm({ ...form, total_employees: e.target.value })} />
                    </div>
                    <div>
                        <label className="label-base">Status</label>
                        <select data-testid="edit-event-status" className="input-base" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                            <option value="draft">draft</option>
                            <option value="pending_review">pending_review</option>
                            <option value="published">published</option>
                            <option value="archived">archived</option>
                        </select>
                    </div>
                    <div>
                        <label className="label-base">Host (owner)</label>
                        <select data-testid="edit-event-host" className="input-base" value={form.host_id} onChange={(e) => setForm({ ...form, host_id: e.target.value })}>
                            {hosts.map((h) => <option key={h.id} value={h.id}>{h.full_name} — {h.email}</option>)}
                        </select>
                    </div>
                    <div className="sm:col-span-2">
                        <label className="label-base">Parking instructions</label>
                        <textarea data-testid="edit-event-parking" rows={2} className="input-base !h-auto py-3" value={form.parking_instructions} onChange={(e) => setForm({ ...form, parking_instructions: e.target.value })} />
                    </div>
                    <div className="sm:col-span-2">
                        <label className="label-base">Setup instructions</label>
                        <textarea data-testid="edit-event-setup" rows={2} className="input-base !h-auto py-3" value={form.setup_instructions} onChange={(e) => setForm({ ...form, setup_instructions: e.target.value })} />
                    </div>
                    <div className="sm:col-span-2">
                        <label className="label-base">Vendor categories & capacity</label>
                        <p className="text-xs text-brand-mute mt-0.5 mb-2">Click to add or remove. Set per-category capacity (default 1) on the rows below.</p>
                        <div className="flex flex-wrap gap-2">
                            {cats.map((c) => {
                                const active = form.category_controls.some((r) => r.category_id === c.id);
                                return (
                                    <button type="button" key={c.id} data-testid={`edit-event-cat-${c.id}`} onClick={() => toggleCat(c.id)}
                                        className={`pill px-3 py-1.5 text-xs font-semibold border ${active ? "bg-brand-coral text-white border-brand-coral" : "bg-white text-brand-ink border-brand-line"}`}>
                                        {c.name}
                                    </button>
                                );
                            })}
                        </div>
                        {form.category_controls.length > 0 && (
                            <div className="rounded-lg border border-brand-line overflow-hidden mt-3">
                                <table className="w-full text-sm">
                                    <thead className="bg-brand-bone">
                                        <tr className="text-left text-xs uppercase tracking-wider text-brand-mute">
                                            <th className="px-3 py-2.5">Category</th>
                                            <th className="px-2 py-2.5">Capacity</th>
                                            <th className="px-2 py-2.5 text-center">Waitlist</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {form.category_controls.map((r) => {
                                            const cat = cats.find((c) => c.id === r.category_id);
                                            return (
                                                <tr key={r.category_id} className="border-t border-brand-line" data-testid={`edit-event-vmrow-${r.category_id}`}>
                                                    <td className="px-3 py-2 font-semibold">{cat?.name || r.category_id}</td>
                                                    <td className="px-2 py-2">
                                                        <input
                                                            data-testid={`edit-event-vmrow-${r.category_id}-max`}
                                                            type="number" min={1}
                                                            className="input-base !h-9 w-20"
                                                            value={r.max_count}
                                                            onChange={(e) => {
                                                                const n = Math.max(1, parseInt(e.target.value || "1", 10));
                                                                updateRow(r.category_id, { max_count: n, desired_count: n });
                                                            }}
                                                        />
                                                    </td>
                                                    <td className="px-2 py-2 text-center">
                                                        <input
                                                            type="checkbox" className="h-4 w-4"
                                                            checked={r.waitlist_enabled}
                                                            onChange={(e) => updateRow(r.category_id, { waitlist_enabled: e.target.checked })}
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
                <div className="rounded-xl border border-brand-line bg-brand-bone/50 p-5">
                    <h4 className="font-heading font-bold text-sm uppercase tracking-wider text-brand-mute mb-3">Event images</h4>
                    <p className="text-xs text-brand-mute mb-3">Hero + supporting photos that appear on the public marketplace and event page.</p>
                    <ImageUpload
                        value={form.images}
                        onChange={(imgs) => setForm({ ...form, images: imgs })}
                        max={6}
                        label="Event photos"
                        testIdPrefix="edit-event-img"
                    />
                </div>
                <div className="rounded-xl border border-brand-line bg-brand-bone/50 p-5">
                    <EventInfoFieldset
                        value={form.event_info}
                        onChange={(next) => setForm({ ...form, event_info: next })}
                        testidPrefix="admin-edit-event-info"
                    />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                    <button onClick={onClose} className="pill px-5 py-3 font-semibold border border-brand-line bg-white">Cancel</button>
                    <button data-testid="edit-event-save" disabled={busy} onClick={save} className="pill btn-coral px-5 py-3 font-semibold disabled:opacity-50">{busy ? "Saving…" : "Save changes"}</button>
                </div>
            </div>
        </ModalShell>
    );
};

const EditVendorModal = ({ vendor, cats, onClose, onSaved }) => {
    const [boothElements, setBoothElements] = useState([]);
    // Normalize interactive_booth_elements: legacy data may store as a list
    // of element ids. Backend migrates on startup but we coerce defensively
    // here so admins can edit either shape without losing data.
    const _ibeRaw = vendor.interactive_booth_elements;
    const _ibeInitial = Array.isArray(_ibeRaw)
        ? _ibeRaw.join(", ")  // ids — placeholder until catalog loads
        : (_ibeRaw || "");
    const [form, setForm] = useState({
        business_name: vendor.business_name || "",
        logo_url: vendor.logo_url || "",
        photos: vendor.photos || [],
        description: vendor.description || "",
        primary_category_id: vendor.primary_category_id || (vendor.categories && vendor.categories[0]) || "",
        service_area: vendor.service_area || "",
        service_radius_miles: vendor.service_radius_miles || 50,
        home_city: vendor.home_city || "",
        home_state: vendor.home_state || "",
        website: vendor.website || "",
        social_links: vendor.social_links || {},
        freebies: vendor.freebies || "",
        raffle_prize: vendor.raffle_prize || "",
        interactive_booth_elements: _ibeInitial,
        // Contact phone + SMS opt-in (mirror of vendor self-service flow).
        // Phone lives on users.phone, surfaced as `vendor.phone` by the list
        // endpoint. We render it as a formatted "(555) 123-4567" string and
        // strip to raw digits on save.
        phone: (() => {
            const d = String(vendor.phone || "").replace(/\D/g, "").replace(/^1/, "").slice(0, 10);
            if (d.length === 0) return "";
            if (d.length < 4) return `(${d}`;
            if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
            return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
        })(),
        sms_opt_in: !!vendor.sms_opt_in,
        admin_notes: vendor.admin_notes || "",
        notification_preferences: vendor.notification_preferences || { categories: [], geography: "", method: "email", frequency: "immediate" },
    });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            const be = await api.get("/booth-elements");
            setBoothElements(be.data);
            // If legacy ids are still in the form (raw array stored as joined
            // ids by the defensive shim above), upgrade to names now that the
            // catalog is loaded.
            if (Array.isArray(_ibeRaw)) {
                const names = _ibeRaw.map((id) => (be.data.find((el) => el.id === id) || {}).name).filter(Boolean).join(", ");
                setForm((f) => ({ ...f, interactive_booth_elements: names }));
            }
        })();
        /* eslint-disable-next-line */
    }, []);

    const appendElement = (name) => {
        const cur = (form.interactive_booth_elements || "").trim();
        const parts = cur ? cur.split(",").map((s) => s.trim()).filter(Boolean) : [];
        if (parts.some((p) => p.toLowerCase() === name.toLowerCase())) return;
        setForm({ ...form, interactive_booth_elements: [...parts, name].join(", ") });
    };

    const toggleNotifCat = (id) => {
        const cur = form.notification_preferences?.categories || [];
        const has = cur.includes(id);
        setForm({ ...form, notification_preferences: {
            ...form.notification_preferences,
            categories: has ? cur.filter((x) => x !== id) : [...cur, id],
        }});
    };

    const formatPhone = (raw) => {
        const d = String(raw || "").replace(/\D/g, "").replace(/^1/, "").slice(0, 10);
        if (d.length === 0) return "";
        if (d.length < 4) return `(${d}`;
        if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
        return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    };

    const save = async () => {
        setBusy(true);
        try {
            // Mirror the backend website normalizer: prepend https:// when
            // the admin typed a bare domain so the saved value AND the local
            // form state are both protocol-prefixed without a round-trip.
            const w = (form.website || "").trim();
            const normalizedWebsite = !w || /^https?:\/\//i.test(w) ? w : "https://" + w;
            const phoneDigits = (form.phone || "").replace(/\D/g, "");
            if (form.sms_opt_in && phoneDigits.length !== 10) {
                toast.error("Add a valid 10-digit US mobile number to opt this vendor in to SMS.");
                setBusy(false);
                return;
            }
            const payload = {
                ...form,
                website: normalizedWebsite,
                phone: phoneDigits,
                sms_opt_in: !!form.sms_opt_in,
            };
            payload.service_radius_miles = parseInt(payload.service_radius_miles, 10) || 50;
            // Drafts (admin-created, not yet claimed) have no user_id. Use
            // the vendor profile id which the backend now also accepts.
            const key = vendor.user_id || vendor.id;
            const res = await api.patch(`/admin/vendor-profiles/${key}`, payload);
            if (res?.data?.website !== undefined && res.data.website !== form.website) {
                setForm((f) => ({ ...f, website: res.data.website }));
            } else if (normalizedWebsite !== form.website) {
                setForm((f) => ({ ...f, website: normalizedWebsite }));
            }
            if (res?.data?.phone !== undefined) {
                setForm((f) => ({ ...f, phone: formatPhone(res.data.phone) }));
            }
            toast.success("Vendor profile updated");
            onSaved();
        } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
        finally { setBusy(false); }
    };
    return (
        <ModalShell wide title="Edit vendor profile" subtitle={`${vendor._user_email || vendor.user?.email || ""}`} onClose={onClose} testId="edit-vendor-modal">
            <div className="space-y-6">
                <div className="rounded-xl border border-brand-line bg-brand-bone/40 p-5 space-y-4">
                    <h4 className="font-heading font-bold text-sm uppercase tracking-wider text-brand-mute">Brand & photos</h4>
                    <SingleImageUpload
                        value={form.logo_url}
                        onChange={(p) => setForm({ ...form, logo_url: p })}
                        label="Business logo"
                        testIdPrefix="edit-vendor-logo"
                    />
                    <ImageUpload
                        value={form.photos || []}
                        onChange={(p) => setForm({ ...form, photos: p })}
                        max={8}
                        label="Booth photos"
                        testIdPrefix="edit-vendor-photos"
                    />
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                        <label className="label-base">Business name</label>
                        <input data-testid="edit-vendor-name" className="input-base" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} />
                    </div>
                    <div className="sm:col-span-2">
                        <label className="label-base">Description</label>
                        <textarea data-testid="edit-vendor-desc" rows={3} className="input-base !h-auto py-3" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                    </div>
                    <div className="sm:col-span-2">
                        <label className="label-base">Primary category</label>
                        <select data-testid="edit-vendor-category" className="input-base" value={form.primary_category_id} onChange={(e) => setForm({ ...form, primary_category_id: e.target.value })}>
                            <option value="">— none —</option>
                            {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <p className="text-xs text-brand-mute mt-1">Admin override — bypasses the vendor's read-only profile lock.</p>
                    </div>
                    <div>
                        <label className="label-base">Home city</label>
                        <input data-testid="edit-vendor-city" className="input-base" value={form.home_city} onChange={(e) => setForm({ ...form, home_city: e.target.value })} />
                    </div>
                    <div>
                        <label className="label-base">Home state</label>
                        <input data-testid="edit-vendor-state" className="input-base" value={form.home_state} onChange={(e) => setForm({ ...form, home_state: e.target.value })} />
                    </div>
                    <div>
                        <label className="label-base">Service area</label>
                        <input data-testid="edit-vendor-area" className="input-base" value={form.service_area} onChange={(e) => setForm({ ...form, service_area: e.target.value })} />
                    </div>
                    <div>
                        <label className="label-base">Travel radius (miles)</label>
                        <input data-testid="edit-vendor-radius" type="number" min={1} className="input-base" value={form.service_radius_miles} onChange={(e) => setForm({ ...form, service_radius_miles: e.target.value })} />
                    </div>
                    <div className="sm:col-span-2">
                        <label className="label-base">Website</label>
                        <input data-testid="edit-vendor-website" type="text" className="input-base" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="mywebsite.com" />
                        <p className="text-xs text-brand-mute mt-1">Enter your website (e.g. mywebsite.com). We'll automatically add https:// if needed.</p>
                    </div>
                    <div className="sm:col-span-2">
                        <label className="label-base">Booth Freebies</label>
                        <input data-testid="edit-vendor-freebies" className="input-base" value={form.freebies} onChange={(e) => setForm({ ...form, freebies: e.target.value })} placeholder="e.g. branded water bottles, $10 gift cards" />
                        <p className="text-xs text-brand-mute mt-1">Small swag every attendee can take home.</p>
                    </div>
                    <div className="sm:col-span-2">
                        <label className="label-base">Raffle Prize</label>
                        <input data-testid="edit-vendor-raffle-prize" className="input-base" value={form.raffle_prize} onChange={(e) => setForm({ ...form, raffle_prize: e.target.value })} placeholder='e.g. "high-end blender", "fitness tracker", "wellness gift basket"' />
                        <p className="text-xs text-brand-mute mt-1">Larger-ticket item attendees enter to win at the booth.</p>
                    </div>
                    <div className="sm:col-span-2">
                        <label className="label-base">Interactive booth elements</label>
                        <textarea
                            data-testid="edit-vendor-ibe"
                            className="input-base !h-auto py-2"
                            rows={2}
                            placeholder="Comma-separated list — e.g. Chair massage station, body composition scans"
                            value={form.interactive_booth_elements || ""}
                            onChange={(e) => setForm({ ...form, interactive_booth_elements: e.target.value })}
                        />
                        {boothElements.length > 0 && (
                            <div className="mt-2">
                                <div className="text-[11px] uppercase tracking-wider text-brand-mute font-bold mb-1.5">Quick picks</div>
                                <div className="flex flex-wrap gap-2">
                                    {boothElements.filter((el) => el.is_active !== false).map((el) => (
                                        <button
                                            type="button"
                                            key={el.id}
                                            data-testid={`edit-vendor-ibe-pick-${el.id}`}
                                            onClick={() => appendElement(el.name)}
                                            className="pill px-3 py-1.5 text-xs font-semibold border border-brand-line bg-white text-brand-ink hover:bg-brand-bone"
                                        >
                                            + {el.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="rounded-xl border border-brand-line bg-brand-bone/40 p-5 space-y-4">
                    <h4 className="font-heading font-bold text-sm uppercase tracking-wider text-brand-mute">Social links</h4>
                    <div className="grid sm:grid-cols-2 gap-3">
                        {[
                            ["linkedin", "LinkedIn"],
                            ["instagram", "Instagram"],
                            ["facebook", "Facebook"],
                            ["x", "X (Twitter)"],
                        ].map(([key, label]) => (
                            <div key={key}>
                                <label className="label-base">{label}</label>
                                <input
                                    data-testid={`edit-vendor-social-${key}`}
                                    className="input-base"
                                    placeholder={`https://`}
                                    value={form.social_links?.[key] || ""}
                                    onChange={(e) => setForm({ ...form, social_links: { ...form.social_links, [key]: e.target.value } })}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                <div className="rounded-xl border border-brand-line bg-brand-bone/40 p-5 space-y-3">
                    <h4 className="font-heading font-bold text-sm uppercase tracking-wider text-brand-mute">Notification preferences</h4>
                    <p className="text-xs text-brand-mute">Which categories should trigger an alert when a new matching event is posted.</p>
                    <div className="flex flex-wrap gap-2">
                        {cats.map((c) => {
                            const active = (form.notification_preferences?.categories || []).includes(c.id);
                            return (
                                <button type="button" key={c.id} data-testid={`edit-vendor-np-${c.id}`} onClick={() => toggleNotifCat(c.id)} className={`pill px-3 py-1.5 text-xs font-semibold border ${active ? "bg-brand-coral text-white border-brand-coral" : "bg-white text-brand-ink border-brand-line"}`}>{c.name}</button>
                            );
                        })}
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <label className="label-base">Method</label>
                            <select data-testid="edit-vendor-np-method" className="input-base" value={form.notification_preferences?.method || "email"} onChange={(e) => setForm({ ...form, notification_preferences: { ...form.notification_preferences, method: e.target.value } })}>
                                <option value="email">Email</option>
                                <option value="in_app">In-app</option>
                            </select>
                        </div>
                        <div>
                            <label className="label-base">Frequency</label>
                            <select data-testid="edit-vendor-np-freq" className="input-base" value={form.notification_preferences?.frequency || "immediate"} onChange={(e) => setForm({ ...form, notification_preferences: { ...form.notification_preferences, frequency: e.target.value } })}>
                                <option value="immediate">Immediate</option>
                                <option value="daily">Daily digest</option>
                                <option value="weekly">Weekly digest</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="rounded-xl border border-brand-line bg-brand-bone/40 p-5 space-y-3" data-testid="edit-vendor-sms-pod">
                    <h4 className="font-heading font-bold text-sm uppercase tracking-wider text-brand-mute">Phone &amp; SMS notifications</h4>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <label className="label-base">Mobile phone</label>
                            <input
                                data-testid="edit-vendor-phone"
                                type="tel"
                                inputMode="tel"
                                autoComplete="tel-national"
                                className="input-base"
                                placeholder="(555) 123-4567"
                                value={form.phone || ""}
                                onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })}
                            />
                            <p className="text-xs text-brand-mute mt-1">US numbers only.</p>
                        </div>
                    </div>
                    <label className="flex items-start gap-3 cursor-pointer select-none">
                        <input
                            data-testid="edit-vendor-sms-consent"
                            type="checkbox"
                            checked={!!form.sms_opt_in}
                            onChange={(e) => setForm({ ...form, sms_opt_in: e.target.checked })}
                            className="mt-1 h-4 w-4 rounded border-brand-line text-brand-forest focus:ring-brand-forest"
                        />
                        <span className="text-xs text-brand-ink leading-relaxed">
                            <strong>SMS opt-in:</strong> records consent to receive transactional event &amp; application texts from HealthFairX (frequency event-driven, msg/data rates may apply, reply HELP/STOP). Use only when the vendor has confirmed verbally.
                        </span>
                    </label>
                </div>

                <div className="rounded-xl border border-brand-line bg-brand-bone/40 p-5 space-y-3" data-testid="edit-vendor-admin-pod">
                    <div className="flex items-start justify-between gap-3">
                        <h4 className="font-heading font-bold text-sm uppercase tracking-wider text-brand-mute">Admin-only</h4>
                        {vendor.claim_status && (
                            <span
                                data-testid={`edit-vendor-status-${vendor.claim_status}`}
                                className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${
                                    vendor.claim_status === "claimed"
                                        ? "border-brand-forest/30 bg-brand-forest/10 text-brand-forest"
                                        : vendor.claim_status === "invitation_sent"
                                        ? "border-amber-300 bg-amber-50 text-amber-700"
                                        : "border-brand-line bg-white text-brand-mute"
                                }`}
                            >
                                {vendor.claim_status.replace("_", " ")}
                            </span>
                        )}
                    </div>
                    <div>
                        <label className="label-base">Internal notes <span className="text-brand-mute font-normal">(never shown publicly)</span></label>
                        <textarea
                            data-testid="edit-vendor-admin-notes"
                            rows={4}
                            className="input-base !h-auto py-3"
                            placeholder="Outreach history, phone-call notes, follow-up dates, etc."
                            value={form.admin_notes || ""}
                            onChange={(e) => setForm({ ...form, admin_notes: e.target.value })}
                        />
                    </div>
                    {vendor.pending_claim_email && (
                        <p className="text-xs text-brand-mute" data-testid="edit-vendor-pending-claim">
                            <strong className="text-brand-ink">Pending claim:</strong> {vendor.pending_claim_email}
                            {vendor.invitation_sent_at && (
                                <> · sent {new Date(vendor.invitation_sent_at).toLocaleDateString()}</>
                            )}
                        </p>
                    )}
                </div>

                <div className="flex justify-end gap-3 pt-2">
                    <button onClick={onClose} className="pill px-5 py-3 font-semibold border border-brand-line bg-white">Cancel</button>
                    <button data-testid="edit-vendor-save" disabled={busy} onClick={save} className="pill btn-coral px-5 py-3 font-semibold disabled:opacity-50">{busy ? "Saving…" : "Save changes"}</button>
                </div>
            </div>
        </ModalShell>
    );
};



// ----------------------------------------------------------------------------- 
// Admin-created drafts: the management panel that lives at the top of the
// Users tab. Surfaces UNCLAIMED + INVITATION_SENT + ARCHIVED profiles with
// status badges and inline actions. Claimed profiles intentionally stay in
// the Users table below — they're edited via the existing per-user button.
// ----------------------------------------------------------------------------- 
const VendorProfilesPanel = ({ profiles, onCreate, onEdit, onSendClaim, onResendClaim, onCopyClaimLink, onArchive, onRestore }) => {
    const drafts = (profiles || []).filter((v) => v.claim_status !== "claimed");
    return (
        <div className="rounded-xl border border-brand-line bg-white p-6" data-testid="vendor-profiles-panel">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h3 className="font-heading text-xl font-bold">Vendor profiles · admin-managed drafts</h3>
                    <p className="text-sm text-brand-mute mt-1 max-w-2xl">
                        Seed the directory with vendors you're recruiting — no email required at creation. When you're ready, add the vendor's email and send a claim invitation so they can take ownership.
                    </p>
                </div>
                <button
                    data-testid="create-vendor-profile-btn"
                    onClick={onCreate}
                    className="pill btn-coral px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2 self-start"
                >
                    <UserPlus size={14} /> Create vendor profile
                </button>
            </div>

            {drafts.length === 0 ? (
                <div className="mt-6 rounded-lg border border-dashed border-brand-line bg-brand-bone/40 p-6 text-center text-sm text-brand-mute">
                    No admin-created drafts yet. Click <strong className="text-brand-ink">Create vendor profile</strong> above to add one.
                </div>
            ) : (
                <div className="mt-6 overflow-hidden rounded-lg border border-brand-line">
                    <table className="w-full text-sm">
                        <thead className="bg-brand-bone text-brand-mute">
                            <tr><th className="text-left px-4 py-3">Business</th><th>Location</th><th>Status</th><th>Pending email</th><th></th></tr>
                        </thead>
                        <tbody>
                            {drafts.map((v) => (
                                <tr key={v.id} className="border-t border-brand-line" data-testid={`vendor-draft-row-${v.id}`}>
                                    <td className="px-4 py-3 font-semibold">
                                        <div>{v.business_name}</div>
                                        {v.is_archived && <div className="text-[11px] uppercase tracking-wider text-brand-mute mt-0.5">archived</div>}
                                    </td>
                                    <td className="text-center text-brand-mute">{[v.home_city, v.home_state].filter(Boolean).join(", ") || "—"}</td>
                                    <td className="text-center">
                                        <span
                                            data-testid={`vendor-draft-status-${v.id}`}
                                            className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${
                                                v.claim_status === "invitation_sent"
                                                    ? "border-amber-300 bg-amber-50 text-amber-700"
                                                    : "border-brand-line bg-white text-brand-mute"
                                            }`}
                                        >
                                            {(v.claim_status || "unclaimed").replace("_", " ")}
                                        </span>
                                    </td>
                                    <td className="text-center text-brand-mute text-xs">{v.pending_claim_email || "—"}</td>
                                    <td className="px-4 py-3 text-right whitespace-nowrap space-x-2">
                                        <button
                                            data-testid={`vendor-draft-edit-${v.id}`}
                                            onClick={() => onEdit(v)}
                                            className="pill px-3 py-1.5 text-xs font-semibold border border-brand-forest/40 text-brand-forest bg-white hover:bg-brand-forest/5 inline-flex items-center gap-1"
                                        >
                                            <Pencil size={12} /> Edit
                                        </button>
                                        {v.claim_status === "unclaimed" && !v.is_archived && (
                                            <button
                                                data-testid={`vendor-draft-send-claim-${v.id}`}
                                                onClick={() => onSendClaim(v)}
                                                className="pill px-3 py-1.5 text-xs font-semibold border border-brand-coral/40 text-brand-coral bg-white hover:bg-brand-coral/5 inline-flex items-center gap-1"
                                            >
                                                <Send size={12} /> Send invitation
                                            </button>
                                        )}
                                        {v.claim_status === "invitation_sent" && !v.is_archived && (
                                            <>
                                                <button
                                                    data-testid={`vendor-draft-resend-claim-${v.id}`}
                                                    onClick={() => onResendClaim(v)}
                                                    className="pill px-3 py-1.5 text-xs font-semibold border border-brand-line bg-white hover:bg-brand-bone inline-flex items-center gap-1"
                                                >
                                                    <Send size={12} /> Resend
                                                </button>
                                                <button
                                                    data-testid={`vendor-draft-copy-link-${v.id}`}
                                                    onClick={() => onCopyClaimLink(v)}
                                                    className="pill px-3 py-1.5 text-xs font-semibold border border-brand-line bg-white hover:bg-brand-bone inline-flex items-center gap-1"
                                                    title="Copy the claim URL to share via Slack/SMS"
                                                >
                                                    <LinkIcon size={12} /> Copy link
                                                </button>
                                            </>
                                        )}
                                        {!v.is_archived ? (
                                            <button
                                                data-testid={`vendor-draft-archive-${v.id}`}
                                                onClick={() => onArchive(v)}
                                                className="pill px-3 py-1.5 text-xs font-semibold border border-brand-line bg-white hover:bg-brand-bone inline-flex items-center gap-1"
                                            >
                                                <Archive size={12} /> Archive
                                            </button>
                                        ) : (
                                            <button
                                                data-testid={`vendor-draft-restore-${v.id}`}
                                                onClick={() => onRestore(v)}
                                                className="pill px-3 py-1.5 text-xs font-semibold border border-brand-line bg-white hover:bg-brand-bone inline-flex items-center gap-1"
                                            >
                                                <ArchiveRestore size={12} /> Restore
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};


// ----------------------------------------------------------------------------- 
// Minimal create-draft modal. We deliberately surface ONLY the most common
// fields here — admin can flesh out the rest via the regular Edit modal once
// the profile exists. Keeps the create flow fast for phone-call workflows.
// ----------------------------------------------------------------------------- 
const CreateVendorModal = ({ cats, onClose, onSaved }) => {
    const [form, setForm] = useState({
        business_name: "",
        description: "",
        primary_category_id: "",
        home_city: "",
        home_state: "",
        website: "",
        admin_notes: "",
    });
    const [busy, setBusy] = useState(false);

    const save = async () => {
        if (!form.business_name.trim()) return toast.error("Business name is required.");
        setBusy(true);
        try {
            const w = (form.website || "").trim();
            const normalizedWebsite = !w || /^https?:\/\//i.test(w) ? w : "https://" + w;
            await api.post("/admin/vendor-profiles", { ...form, website: normalizedWebsite });
            toast.success("Draft profile created");
            onSaved();
        } catch (err) { toast.error(err?.response?.data?.detail || "Create failed"); }
        finally { setBusy(false); }
    };

    return (
        <ModalShell title="Create vendor profile" subtitle="Unclaimed until a vendor accepts the invitation" onClose={onClose} testId="create-vendor-modal">
            <div className="space-y-4">
                <div>
                    <label className="label-base">Business name <span className="text-brand-coral">*</span></label>
                    <input data-testid="create-vendor-name" className="input-base" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} />
                </div>
                <div>
                    <label className="label-base">Short description</label>
                    <textarea data-testid="create-vendor-desc" rows={3} className="input-base !h-auto py-3" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="One sentence the public will see on the vendor card." />
                </div>
                <div>
                    <label className="label-base">Primary category</label>
                    <select data-testid="create-vendor-category" className="input-base" value={form.primary_category_id} onChange={(e) => setForm({ ...form, primary_category_id: e.target.value })}>
                        <option value="">— pick later —</option>
                        {(cats || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                        <label className="label-base">Home city</label>
                        <input data-testid="create-vendor-city" className="input-base" value={form.home_city} onChange={(e) => setForm({ ...form, home_city: e.target.value })} />
                    </div>
                    <div>
                        <label className="label-base">Home state</label>
                        <input data-testid="create-vendor-state" className="input-base" value={form.home_state} onChange={(e) => setForm({ ...form, home_state: e.target.value })} />
                    </div>
                </div>
                <div>
                    <label className="label-base">Website</label>
                    <input data-testid="create-vendor-website" className="input-base" placeholder="mywebsite.com" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
                </div>
                <div>
                    <label className="label-base">Internal notes <span className="text-brand-mute font-normal">(admin only)</span></label>
                    <textarea data-testid="create-vendor-notes" rows={3} className="input-base !h-auto py-3" placeholder="Outreach history, phone-call notes." value={form.admin_notes} onChange={(e) => setForm({ ...form, admin_notes: e.target.value })} />
                </div>
                <p className="text-xs text-brand-mute">
                    The profile will be created as <strong>unclaimed</strong> and appear in the public directory immediately. Add an email + send a claim invitation once you've spoken to the vendor.
                </p>
                <div className="flex justify-end gap-3 pt-2">
                    <button onClick={onClose} className="pill px-5 py-3 font-semibold border border-brand-line bg-white">Cancel</button>
                    <button data-testid="create-vendor-save" disabled={busy} onClick={save} className="pill btn-coral px-5 py-3 font-semibold disabled:opacity-50">{busy ? "Creating…" : "Create draft"}</button>
                </div>
            </div>
        </ModalShell>
    );
};


// ----------------------------------------------------------------------------- 
// Claim invitation modal — collects vendor email + optional linked event so
// the vendor lands on that event page after they claim.
// ----------------------------------------------------------------------------- 
const SendClaimModal = ({ vendor, events, onClose, onSaved }) => {
    const [form, setForm] = useState({
        email: vendor.pending_claim_email || "",
        event_id: vendor.pending_claim_event_id || "",
    });
    const [busy, setBusy] = useState(false);
    const eligibleEvents = (events || []).filter((e) => e.status === "published" || e.status === "registration_open");

    const send = async () => {
        if (!form.email.trim()) return toast.error("Vendor email is required.");
        setBusy(true);
        try {
            const res = await api.post(`/admin/vendor-profiles/${vendor.id}/send-claim`, {
                email: form.email.trim(),
                event_id: form.event_id || null,
            });
            // Best-effort auto-copy the claim URL so the admin can paste it
            // anywhere if the email is blocked (preview allowlist, spam, bounce).
            const url = res.data?.claim_url;
            if (url) {
                try {
                    await navigator.clipboard.writeText(url);
                    toast.success(`Invitation sent to ${form.email.trim()} — claim link copied to clipboard.`);
                } catch {
                    toast.success(`Invitation sent to ${form.email.trim()}.`);
                    // Surface URL on a longer-lived path so it's still grabbable
                    setTimeout(() => window.prompt("Copy this claim link:", url), 200);
                }
            } else {
                toast.success(`Invitation sent to ${form.email.trim()}`);
            }
            onSaved();
        } catch (err) { toast.error(err?.response?.data?.detail || "Send failed"); }
        finally { setBusy(false); }
    };

    return (
        <ModalShell title={`Send claim invitation`} subtitle={vendor.business_name} onClose={onClose} testId="send-claim-modal">
            <div className="space-y-4">
                <div>
                    <label className="label-base">Vendor email <span className="text-brand-coral">*</span></label>
                    <input
                        data-testid="send-claim-email"
                        type="email"
                        className="input-base"
                        placeholder="vendor@example.com"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />
                </div>
                <div>
                    <label className="label-base">Link to an event <span className="text-brand-mute font-normal">(optional)</span></label>
                    <select data-testid="send-claim-event" className="input-base" value={form.event_id} onChange={(e) => setForm({ ...form, event_id: e.target.value })}>
                        <option value="">— don't link to an event —</option>
                        {eligibleEvents.map((ev) => (
                            <option key={ev.id} value={ev.id}>{ev.name} · {fmtDate(ev.event_date)}</option>
                        ))}
                    </select>
                    <p className="text-xs text-brand-mute mt-1">After claiming, the vendor will be redirected straight to this event.</p>
                </div>
                <p className="text-xs text-brand-mute">
                    The invitation link expires in 30 days. You can resend it any time — the prior link is automatically revoked.
                </p>
                <div className="flex justify-end gap-3 pt-2">
                    <button onClick={onClose} className="pill px-5 py-3 font-semibold border border-brand-line bg-white">Cancel</button>
                    <button data-testid="send-claim-submit" disabled={busy} onClick={send} className="pill btn-coral px-5 py-3 font-semibold disabled:opacity-50 inline-flex items-center gap-2">
                        <Send size={14} /> {busy ? "Sending…" : "Send invitation"}
                    </button>
                </div>
            </div>
        </ModalShell>
    );
};



// ----------------------------------------------------------------------------- 
// Booth elements — admin-managed pick list. Vendors choose from these on
// their profile and the names auto-populate their expression-of-interest note.
// ----------------------------------------------------------------------------- 
const BoothElementsTab = () => {
    const [items, setItems] = useState([]);
    const [draft, setDraft] = useState({ name: "", is_active: true });
    const [edits, setEdits] = useState({});

    const load = async () => {
        const r = await api.get("/admin/booth-elements");
        setItems(r.data);
        setEdits(Object.fromEntries(r.data.map((el) => [el.id, { name: el.name, is_active: el.is_active }])));
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        const name = (draft.name || "").trim();
        if (!name) return toast.error("Name required");
        try {
            await api.post("/admin/booth-elements", { name, is_active: draft.is_active });
            toast.success("Added");
            setDraft({ name: "", is_active: true });
            await load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Add failed"); }
    };

    const save = async (id) => {
        try {
            await api.patch(`/admin/booth-elements/${id}`, edits[id]);
            toast.success("Saved");
            await load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    };

    const remove = async (id, name) => {
        if (!window.confirm(`Delete "${name}"? Existing vendor profiles that reference this element will keep the id but stop displaying the chip.`)) return;
        try {
            await api.delete(`/admin/booth-elements/${id}`);
            toast.success("Deleted");
            await load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
    };

    return (
        <div className="space-y-4" data-testid="admin-booth-elements">
            <div className="rounded-xl border border-brand-line bg-brand-bone/60 p-5">
                <h3 className="font-heading font-bold">Interactive booth elements</h3>
                <p className="text-sm text-brand-mute mt-1">
                    The chips vendors can attach to their profile. The names show up automatically in the "pitch to host" note when a vendor expresses interest in an event — driving more freebies and richer booths.
                </p>
            </div>

            <div className="rounded-xl border border-brand-line bg-white p-5">
                <h4 className="font-semibold mb-3">Add a new element</h4>
                <div className="flex flex-wrap gap-3">
                    <input
                        data-testid="be-new-name"
                        className="input-base max-w-md"
                        placeholder="e.g. On-site flu shots"
                        value={draft.name}
                        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    />
                    <button data-testid="be-new-add" onClick={create} className="pill btn-coral h-11 px-5 font-semibold">Add</button>
                </div>
            </div>

            <div className="rounded-xl border border-brand-line bg-white overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-brand-bone text-brand-mute">
                        <tr>
                            <th className="text-left px-4 py-3">Name</th>
                            <th className="text-center">Active</th>
                            <th className="text-right pr-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((el) => {
                            const d = edits[el.id] || { name: el.name, is_active: el.is_active };
                            const dirty = d.name !== el.name || d.is_active !== el.is_active;
                            return (
                                <tr key={el.id} className="border-t border-brand-line" data-testid={`be-row-${el.id}`}>
                                    <td className="px-4 py-3">
                                        <input
                                            data-testid={`be-name-${el.id}`}
                                            className="input-base"
                                            value={d.name}
                                            onChange={(e) => setEdits({ ...edits, [el.id]: { ...d, name: e.target.value } })}
                                        />
                                    </td>
                                    <td className="text-center">
                                        <input
                                            data-testid={`be-active-${el.id}`}
                                            type="checkbox"
                                            checked={d.is_active}
                                            onChange={(e) => setEdits({ ...edits, [el.id]: { ...d, is_active: e.target.checked } })}
                                        />
                                    </td>
                                    <td className="text-right pr-4 whitespace-nowrap space-x-2">
                                        <button data-testid={`be-save-${el.id}`} disabled={!dirty} onClick={() => save(el.id)} className="pill btn-forest px-3 py-1.5 text-xs font-semibold disabled:opacity-40">Save</button>
                                        <button data-testid={`be-delete-${el.id}`} onClick={() => remove(el.id, el.name)} className="pill px-3 py-1.5 text-xs font-semibold text-brand-coral border border-brand-coral/40 bg-white hover:bg-brand-coral/5">Delete</button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {items.length === 0 && <div className="p-8 text-center text-brand-mute">No booth elements yet.</div>}
            </div>
        </div>
    );
};

// ----------------------------------------------------------------------------- 
// Refunds — admin queue. Partial-refund cancellations require admin approval.
// Full/zero refunds are auto-issued at cancel time and appear here as a log.
// ----------------------------------------------------------------------------- 
const RefundsTab = () => {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("pending");
    const [busy, setBusy] = useState({});

    const load = async () => {
        const params = filter ? { status: filter } : {};
        const r = await api.get("/admin/refunds", { params });
        setItems(r.data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const approve = async (refund) => {
        const noteAmt = window.prompt(
            `Approving ${dollars(refund.amount_cents)} refund for ${refund.vendor?.email || "vendor"}.\n\nLeave blank to issue as-is, or enter a different amount in DOLLARS (e.g. 75 for $75):`,
            ""
        );
        if (noteAmt === null) return;
        const payload = {};
        if (noteAmt.trim()) {
            const v = parseFloat(noteAmt);
            if (Number.isNaN(v) || v < 0) return toast.error("Invalid amount");
            payload.amount_cents = Math.round(v * 100);
        }
        setBusy((b) => ({ ...b, [refund.id]: true }));
        try {
            await api.post(`/admin/refunds/${refund.id}/approve`, payload);
            toast.success("Refund issued via Stripe");
            await load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Approve failed"); }
        finally { setBusy((b) => ({ ...b, [refund.id]: false })); }
    };

    const deny = async (refund) => {
        const note = window.prompt(`Deny this refund? Optional note (audit log):`, "");
        if (note === null) return;
        setBusy((b) => ({ ...b, [refund.id]: true }));
        try {
            await api.post(`/admin/refunds/${refund.id}/deny`, { note });
            toast.success("Refund denied");
            await load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Deny failed"); }
        finally { setBusy((b) => ({ ...b, [refund.id]: false })); }
    };

    return (
        <div className="space-y-4" data-testid="admin-refunds">
            <div className="rounded-xl border border-brand-line bg-brand-bone/60 p-5">
                <h3 className="font-heading font-bold">Refunds</h3>
                <p className="text-sm text-brand-mute mt-1">
                    Partial refunds (50%, vendor cancellations 2–4 weeks before event) need admin approval. Full and zero-dollar decisions are auto-issued; this list is the audit log.
                </p>
            </div>

            <div className="flex gap-2">
                {["pending", "issued", "denied", ""].map((s) => (
                    <button
                        key={s || "all"}
                        data-testid={`refunds-filter-${s || "all"}`}
                        onClick={() => setFilter(s)}
                        className={`pill px-4 py-1.5 text-xs font-semibold border ${filter === s ? "bg-brand-forest text-white border-brand-forest" : "bg-white text-brand-ink border-brand-line"}`}
                    >
                        {s || "All"}
                    </button>
                ))}
            </div>

            <div className="rounded-xl border border-brand-line bg-white overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-brand-bone text-brand-mute">
                        <tr>
                            <th className="text-left px-4 py-3">Vendor</th>
                            <th className="text-left">Event</th>
                            <th className="text-left">Amount</th>
                            <th className="text-left">Tier</th>
                            <th className="text-left">Status</th>
                            <th className="text-left">Reason</th>
                            <th className="text-right pr-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((r) => (
                            <tr key={r.id} className="border-t border-brand-line align-top" data-testid={`refund-row-${r.id}`}>
                                <td className="px-4 py-3">
                                    <div className="font-semibold">{r.vendor?.full_name || "—"}</div>
                                    <div className="text-xs text-brand-mute">{r.vendor?.email}</div>
                                </td>
                                <td className="text-sm">
                                    <div className="font-semibold">{r.event?.name || "—"}</div>
                                    <div className="text-xs text-brand-mute">{r.event?.event_date}</div>
                                </td>
                                <td className="font-heading font-bold">{dollars(r.amount_cents)}</td>
                                <td className="text-xs">
                                    <span className="chip">{r.policy_tier}</span>
                                </td>
                                <td>
                                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-md ${r.status === "issued" ? "bg-emerald-100 text-emerald-700" : r.status === "denied" ? "bg-slate-100 text-slate-600" : "bg-amber-100 text-amber-700"}`}>
                                        {r.status}
                                    </span>
                                </td>
                                <td className="text-xs text-brand-ink/70 max-w-xs">
                                    <div className="line-clamp-3">{r.reason}</div>
                                </td>
                                <td className="text-right pr-4 whitespace-nowrap space-x-2">
                                    {r.status === "pending" ? (
                                        <>
                                            <button data-testid={`refund-approve-${r.id}`} disabled={busy[r.id]} onClick={() => approve(r)} className="pill btn-forest px-3 py-1.5 text-xs font-semibold disabled:opacity-40">Approve</button>
                                            <button data-testid={`refund-deny-${r.id}`} disabled={busy[r.id]} onClick={() => deny(r)} className="pill px-3 py-1.5 text-xs font-semibold text-brand-coral border border-brand-coral/40 bg-white">Deny</button>
                                        </>
                                    ) : (
                                        <span className="text-xs text-brand-mute italic">
                                            {r.stripe_refund_id ? `Stripe: ${r.stripe_refund_id.slice(0, 14)}…` : "—"}
                                        </span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {items.length === 0 && <div className="p-8 text-center text-brand-mute">No refunds.</div>}
            </div>
        </div>
    );
};


// ----------------------------------------------------------------------------- 
// Email safety / suppression list. Hard-bounces, complaints, and admin-added
// addresses live here. Every outgoing email is checked against this list.
// ----------------------------------------------------------------------------- 
const SuppressionTab = () => {
    const [items, setItems] = useState([]);
    const [draft, setDraft] = useState({ email: "", reason: "" });

    const load = async () => {
        const r = await api.get("/admin/suppression");
        setItems(r.data);
    };
    useEffect(() => { load(); }, []);

    const add = async () => {
        const email = (draft.email || "").trim().toLowerCase();
        if (!email || !email.includes("@")) return toast.error("Valid email required");
        try {
            await api.post("/admin/suppression", { email, reason: draft.reason || "manual" });
            toast.success("Added to suppression list");
            setDraft({ email: "", reason: "" });
            await load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Add failed"); }
    };

    const remove = async (email) => {
        if (!window.confirm(`Remove ${email} from suppression list? Emails to this address will resume.`)) return;
        try {
            await api.delete(`/admin/suppression/${encodeURIComponent(email)}`);
            toast.success("Removed");
            await load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Remove failed"); }
    };

    return (
        <div className="space-y-4" data-testid="admin-suppression">
            <div className="rounded-xl border border-brand-line bg-brand-bone/60 p-5">
                <h3 className="font-heading font-bold">Email safety controls</h3>
                <p className="text-sm text-brand-mute mt-1">
                    Addresses on this list never receive HealthFairX emails. We auto-add hard bounces and complaints from Resend. Manual additions are also accepted (e.g. when a customer asks to be removed).
                </p>
                <p className="text-xs text-brand-mute mt-2">
                    Resend webhook URL — point Resend's dashboard at: <code className="bg-white border border-brand-line px-2 py-0.5 rounded">/api/webhook/resend</code>
                </p>
            </div>

            <div className="rounded-xl border border-brand-line bg-white p-5">
                <h4 className="font-semibold mb-3">Add address</h4>
                <div className="flex flex-wrap gap-3">
                    <input data-testid="supp-new-email" type="email" className="input-base max-w-sm" placeholder="bounced@example.com" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
                    <input data-testid="supp-new-reason" className="input-base max-w-xs" placeholder="reason (optional)" value={draft.reason} onChange={(e) => setDraft({ ...draft, reason: e.target.value })} />
                    <button data-testid="supp-add" onClick={add} className="pill btn-coral h-11 px-5 font-semibold">Add</button>
                </div>
            </div>

            <div className="rounded-xl border border-brand-line bg-white overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-brand-bone text-brand-mute">
                        <tr>
                            <th className="text-left px-4 py-3">Email</th>
                            <th className="text-left">Reason</th>
                            <th className="text-left">Source</th>
                            <th className="text-left">Added</th>
                            <th className="text-right pr-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((s) => (
                            <tr key={s.email} className="border-t border-brand-line" data-testid={`supp-row-${s.email}`}>
                                <td className="px-4 py-3 font-semibold">{s.email}</td>
                                <td className="text-xs text-brand-mute">{s.reason}</td>
                                <td className="text-xs"><span className="chip">{s.source || "manual"}</span></td>
                                <td className="text-xs text-brand-mute">{s.added_at ? new Date(s.added_at).toLocaleString() : ""}</td>
                                <td className="text-right pr-4">
                                    <button data-testid={`supp-remove-${s.email}`} onClick={() => remove(s.email)} className="pill px-3 py-1.5 text-xs font-semibold text-brand-coral border border-brand-coral/40 bg-white">Remove</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {items.length === 0 && <div className="p-8 text-center text-brand-mute">No suppressed addresses.</div>}
            </div>
        </div>
    );
};


// ----------------------------------------------------------------------------- 
// Reviews moderation queue. Reviews are pending until approved, then become
// public on the vendor's profile and feed the average rating.
// ----------------------------------------------------------------------------- 
const ReviewsTab = () => {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("pending");
    const [busy, setBusy] = useState({});

    const load = async () => {
        const params = filter ? { status: filter } : {};
        const r = await api.get("/admin/reviews", { params });
        setItems(r.data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const act = async (review, action) => {
        const note = action === "approve" ? "" : (window.prompt(`Optional note for "${action}":`, "") ?? "");
        setBusy((b) => ({ ...b, [review.id]: true }));
        try {
            await api.post(`/admin/reviews/${review.id}/${action}`, { note });
            toast.success(`Review ${action}d`);
            await load();
        } catch (err) { toast.error(err?.response?.data?.detail || `${action} failed`); }
        finally { setBusy((b) => ({ ...b, [review.id]: false })); }
    };

    return (
        <div className="space-y-4" data-testid="admin-reviews">
            <div className="rounded-xl border border-brand-line bg-brand-bone/60 p-5">
                <h3 className="font-heading font-bold">Vendor reviews moderation</h3>
                <p className="text-sm text-brand-mute mt-1">Reviews are only public once approved. Hide for off-topic or low-quality content; flag for further review without exposing the vendor.</p>
            </div>

            <div className="flex gap-2">
                {["pending", "approved", "hidden", "flagged", ""].map((s) => (
                    <button key={s || "all"} data-testid={`reviews-filter-${s || "all"}`} onClick={() => setFilter(s)}
                        className={`pill px-4 py-1.5 text-xs font-semibold border ${filter === s ? "bg-brand-forest text-white border-brand-forest" : "bg-white text-brand-ink border-brand-line"}`}>
                        {s || "All"}
                    </button>
                ))}
            </div>

            <div className="space-y-3">
                {items.map((r) => (
                    <div key={r.id} className="rounded-xl border border-brand-line bg-white p-5" data-testid={`admin-review-${r.id}`}>
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div className="min-w-0">
                                <div className="font-heading font-bold">{r.vendor?.business_name || "Vendor"}</div>
                                <div className="text-xs text-brand-mute">
                                    Reviewed by {r.host?.full_name || r.host?.email} · {r.event?.name} ({r.event?.event_date})
                                </div>
                                <div className="mt-2 flex items-center gap-1 text-amber-500">
                                    {Array.from({ length: r.stars }).map((_, i) => (<span key={i}>★</span>))}
                                    {Array.from({ length: 5 - r.stars }).map((_, i) => (<span key={`e${i}`} className="text-brand-line">★</span>))}
                                    <span className="ml-2 text-xs font-semibold text-brand-mute">would invite again: {r.would_invite_again}</span>
                                </div>
                                {r.text && <p className="text-sm text-brand-ink/85 mt-2 whitespace-pre-line">{r.text}</p>}
                                {(r.tags || []).length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-2">
                                        {r.tags.map((t) => <span key={t} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-brand-bone text-brand-ink">{t}</span>)}
                                    </div>
                                )}
                                {r.reported_count > 0 && (
                                    <div className="mt-2 text-xs text-amber-700">⚠ Reported {r.reported_count}× by the vendor. Latest reason: {r.last_report_reason}</div>
                                )}
                            </div>
                            <div className="flex flex-col gap-1 shrink-0">
                                <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded text-center ${r.status === "approved" ? "bg-emerald-100 text-emerald-700" : r.status === "hidden" ? "bg-slate-100 text-slate-700" : r.status === "flagged" ? "bg-amber-100 text-amber-700" : "bg-brand-bone text-brand-mute"}`}>{r.status}</span>
                                {r.status !== "approved" && <button data-testid={`review-approve-${r.id}`} disabled={busy[r.id]} onClick={() => act(r, "approve")} className="pill btn-forest px-3 py-1.5 text-xs font-semibold disabled:opacity-40">Approve</button>}
                                {r.status !== "hidden" && <button data-testid={`review-hide-${r.id}`} disabled={busy[r.id]} onClick={() => act(r, "hide")} className="pill px-3 py-1.5 text-xs font-semibold border border-brand-line bg-white">Hide</button>}
                                {r.status !== "flagged" && <button data-testid={`review-flag-${r.id}`} disabled={busy[r.id]} onClick={() => act(r, "flag")} className="pill px-3 py-1.5 text-xs font-semibold text-amber-700 border border-amber-300 bg-white">Flag</button>}
                            </div>
                        </div>
                    </div>
                ))}
                {items.length === 0 && <div className="rounded-xl border border-brand-line bg-white p-8 text-center text-brand-mute">No reviews.</div>}
            </div>
        </div>
    );
};


// SEO dashboard — aggregate page-views by route, plus sitemap regen + indexable URL list.
const SeoTab = () => {
    const [stats, setStats] = useState(null);
    const [days, setDays] = useState(30);
    const [busy, setBusy] = useState(false);
    const load = async () => {
        const r = await api.get("/admin/seo/stats", { params: { days } });
        setStats(r.data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);
    const regen = async () => {
        setBusy(true);
        try {
            await api.post("/admin/seo/regenerate-sitemap");
            toast.success("Sitemap regenerated.");
        } catch (err) { toast.error("Regen failed: " + (err?.response?.data?.detail || err.message)); }
        finally { setBusy(false); }
    };
    return (
        <div className="space-y-6" data-testid="admin-seo">
            <div className="rounded-xl border border-brand-line bg-brand-bone p-5">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <h3 className="font-heading font-bold">SEO traffic</h3>
                        <p className="text-sm text-brand-mute mt-1">Server-side page-view counters (bucketed daily). Use this alongside GA4 / Plausible for cross-checking.</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <select data-testid="seo-window" className="input-base !h-9 !w-auto" value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))}>
                            <option value={7}>Last 7 days</option>
                            <option value={30}>Last 30 days</option>
                            <option value={90}>Last 90 days</option>
                            <option value={365}>Last 365 days</option>
                        </select>
                        <button data-testid="seo-regen-sitemap" disabled={busy} onClick={regen} className="pill btn-coral px-4 py-1.5 text-sm font-semibold disabled:opacity-50">{busy ? "…" : "Regenerate sitemap"}</button>
                    </div>
                </div>
            </div>

            {stats ? (
                <>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="rounded-xl border border-brand-line bg-white p-5">
                            <div className="label-base">Total page views</div>
                            <div className="font-heading text-3xl font-extrabold text-brand-forest mt-1" data-testid="seo-total-views">{stats.total_views.toLocaleString()}</div>
                            <div className="text-xs text-brand-mute mt-1">Across all public routes</div>
                        </div>
                        {stats.by_bucket.slice(0, 3).map((b) => (
                            <div key={b.bucket} className="rounded-xl border border-brand-line bg-white p-5" data-testid={`seo-bucket-${b.bucket}`}>
                                <div className="label-base">{b.bucket.replace(/_/g, " ")}</div>
                                <div className="font-heading text-3xl font-extrabold text-brand-forest mt-1">{b.views.toLocaleString()}</div>
                            </div>
                        ))}
                    </div>

                    <div className="rounded-xl border border-brand-line bg-white p-5">
                        <div className="flex items-baseline justify-between gap-3">
                            <h4 className="font-heading font-bold">Top routes</h4>
                            <a href="/sitemap.xml" target="_blank" rel="noreferrer" className="text-sm text-brand-forest hover:underline">View sitemap.xml ↗</a>
                        </div>
                        <table className="w-full mt-4 text-sm">
                            <thead className="text-xs uppercase tracking-wider text-brand-mute">
                                <tr><th className="text-left py-2">Route</th><th className="text-right">Views</th></tr>
                            </thead>
                            <tbody data-testid="seo-top-routes">
                                {stats.by_route.length === 0 && <tr><td colSpan={2} className="py-6 text-center text-brand-mute">No page views recorded yet. Counters increment as users land on public pages.</td></tr>}
                                {stats.by_route.map((r) => (
                                    <tr key={r.route} className="border-t border-brand-line">
                                        <td className="py-2 font-mono text-xs truncate max-w-[440px]"><a href={r.route} target="_blank" rel="noreferrer" className="hover:text-brand-forest hover:underline">{r.route}</a></td>
                                        <td className="py-2 text-right tabular-nums">{r.views.toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            ) : <div className="rounded-xl border border-brand-line bg-white p-10 text-center text-brand-mute">Loading…</div>}
        </div>
    );
};


// Stripe wiring health-check banner. Pulls from /api/admin/stripe/status
// so admins can confirm at a glance which mode the deploy is running in
// without reading supervisor logs. Color codes:
//   placeholder → amber  (shared Emergent key, no real payments)
//   test        → blue   (own test mode — safe to run E2E flows)
//   live        → green  (real money moves)
//   unset/other → red    (broken config)
const StripeStatusBanner = ({ status }) => {
    if (!status) return null;
    const palette = {
        live:        { bg: "bg-brand-mint", text: "text-brand-forest",  label: "LIVE",        hint: "Real money will move on every successful checkout." },
        test:        { bg: "bg-blue-50",    text: "text-blue-700",      label: "TEST",        hint: "Your own Stripe test mode — safe for E2E testing." },
        placeholder: { bg: "bg-amber-50",   text: "text-amber-700",     label: "PLACEHOLDER", hint: "Shared Emergent sandbox key. Replace before real payments." },
        unset:       { bg: "bg-rose-50",    text: "text-rose-700",      label: "NOT SET",     hint: "STRIPE_API_KEY is empty — payment endpoints will 500." },
        unknown:     { bg: "bg-rose-50",    text: "text-rose-700",      label: "UNKNOWN",     hint: "Key prefix not recognized — verify the value." },
    };
    const p = palette[status.mode] || palette.unknown;
    const mm = status.multi_mode || {};
    const multiSet = mm.test_key_set || mm.live_key_set;
    return (
        <div className={`rounded-xl border border-brand-line ${p.bg} p-5 flex items-start gap-4`} data-testid="admin-stripe-banner">
            <div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-brand-mute">Stripe</div>
                <div className={`font-heading text-2xl font-extrabold ${p.text} mt-1`} data-testid="admin-stripe-mode">{p.label}</div>
                {status.host && (
                    <div className="text-[10px] text-brand-mute font-mono mt-1">
                        host: <span className={status.is_live_host ? "text-brand-forest font-bold" : ""}>{status.host}</span>
                    </div>
                )}
            </div>
            <div className="flex-1">
                <div className="text-sm text-brand-ink/85">{p.hint}</div>
                <div className="text-xs text-brand-mute mt-2 font-mono">
                    {status.api_key_preview || "—"}
                    {" · webhook secret "}
                    {status.webhook_secret_set ? "configured" : <span className="text-rose-600 font-bold">missing</span>}
                    {" · endpoint "}<span>{status.webhook_endpoint}</span>
                </div>
                {multiSet && (
                    <div className="mt-3 text-xs text-brand-mute" data-testid="admin-stripe-multimode">
                        <div className="font-semibold text-brand-ink/80">Multi-mode keys configured:</div>
                        <div className="font-mono mt-1 grid sm:grid-cols-2 gap-x-4">
                            <span>TEST key: <span className={mm.test_key_set ? "text-brand-forest font-bold" : "text-rose-600 font-bold"}>{mm.test_key_set ? "set" : "missing"}</span></span>
                            <span>TEST webhook: <span className={mm.test_webhook_secret_set ? "text-brand-forest font-bold" : "text-rose-600 font-bold"}>{mm.test_webhook_secret_set ? "set" : "missing"}</span></span>
                            <span>LIVE key: <span className={mm.live_key_set ? "text-brand-forest font-bold" : "text-rose-600 font-bold"}>{mm.live_key_set ? "set" : "missing"}</span></span>
                            <span>LIVE webhook: <span className={mm.live_webhook_secret_set ? "text-brand-forest font-bold" : "text-rose-600 font-bold"}>{mm.live_webhook_secret_set ? "set" : "missing"}</span></span>
                        </div>
                        {status.live_hosts_allowlist?.length > 0 && (
                            <div className="font-mono mt-1.5">live hosts: {status.live_hosts_allowlist.join(", ")}</div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

